import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueries } from '@tanstack/react-query'
import { api, OpenPosition } from '../services/api'
import { ArrowLeft, ChevronDown, ChevronRight, GripVertical } from 'lucide-react'

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return `${(value * 100).toFixed(2)}%`
}

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Calculate adjusted quantity for positions, applying multiplier for options
// Returns "complex" if there are both long and short positions
function calcAdjustedQty(positions: Array<{ quantity: number; multiplier?: number; assetCategory?: string }>): number | 'complex' {
  if (positions.length === 0) return 0

  let hasLong = false
  let hasShort = false
  let total = 0

  for (const p of positions) {
    const isOption = (p.assetCategory || '').includes('Options')
    const multiplier = p.multiplier ?? 1
    const adjustedQty = isOption ? p.quantity * multiplier : p.quantity

    if (p.quantity > 0) hasLong = true
    if (p.quantity < 0) hasShort = true

    total += adjustedQty
  }

  if (hasLong && hasShort) return 'complex'
  return total
}

// Parse option symbol to extract details
function parseOptionSymbol(symbol: string): { underlying: string; expiration: Date; strike: number; type: 'C' | 'P' } | null {
  // Format: "AAPL 250117C00200000" or "AAPL 21JAN28 100 C"
  const parts = symbol.split(' ')
  if (parts.length < 2) return null

  const underlying = parts[0]

  // Try OCC format first: "AAPL 250117C00200000"
  if (parts.length === 2) {
    const optPart = parts[1]
    // YYMMDD + C/P + 8-digit strike
    const match = optPart.match(/^(\d{6})([CP])(\d{8})$/)
    if (match) {
      const [, dateStr, typeStr, strikeStr] = match
      const year = 2000 + parseInt(dateStr.substring(0, 2))
      const month = parseInt(dateStr.substring(2, 4)) - 1
      const day = parseInt(dateStr.substring(4, 6))
      const strike = parseInt(strikeStr) / 1000
      return {
        underlying,
        expiration: new Date(year, month, day),
        strike,
        type: typeStr as 'C' | 'P'
      }
    }
  }

  // Try IBKR format: "AAPL 21JAN28 100 C"
  if (parts.length >= 4) {
    const dateStr = parts[1]
    const strike = parseFloat(parts[2])
    const type = parts[3] as 'C' | 'P'

    // Parse date like "21JAN28"
    const dateMatch = dateStr.match(/^(\d{2})([A-Z]{3})(\d{2})$/)
    if (dateMatch) {
      const [, day, monthStr, year] = dateMatch
      const months: Record<string, number> = {
        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
      }
      return {
        underlying,
        expiration: new Date(2000 + parseInt(year), months[monthStr], parseInt(day)),
        strike,
        type
      }
    }
  }

  return null
}

// Sort securities: covered calls first, then options (latest maturity, lowest strike), then stock
function sortSecurities(securities: SecurityItem[]): SecurityItem[] {
  return [...securities].sort((a, b) => {
    // Covered calls come first
    if (a.type === 'covered_call' && b.type !== 'covered_call') return -1
    if (a.type !== 'covered_call' && b.type === 'covered_call') return 1

    // Stock comes last
    if (a.type === 'stock' && b.type !== 'stock') return 1
    if (a.type !== 'stock' && b.type === 'stock') return -1
    if (a.type === 'stock' && b.type === 'stock') return 0

    // Both are options or covered calls - sort by strike (lowest first), then expiration (earliest first)
    const aStrike = a.strike || 0
    const bStrike = b.strike || 0
    if (aStrike !== bStrike) return aStrike - bStrike // Lowest strike first
    const aExp = a.expiration?.getTime() || 0
    const bExp = b.expiration?.getTime() || 0
    return aExp - bExp // Earliest expiration first
  })
}

interface SecurityItem {
  id: string
  symbol: string
  type: 'stock' | 'call' | 'put' | 'covered_call'
  displayName: string
  expiration?: Date
  strike?: number
  // For covered calls, reference to the call option
  callSymbol?: string
  // Current price (for calculating order value) - in the security's native currency
  price: number | null
  // Currency of the price (e.g., 'USD', 'HKD')
  currency: string
  // Per-unit contributions for each exposure type (as decimal, per account)
  // These are calculated based on the security's price/delta relative to portfolio value
  perUnitContributions: Record<number, {
    position: number  // value / portfolioValue per unit
    nominal: number   // nominal_exposure / portfolioValue per unit
    delta: number     // delta_adjusted_exposure / portfolioValue per unit
    nc: number        // nc_exposure / portfolioValue per unit
  }>
}

interface AccountExposure {
  accountNumber: string
  accountId: number
  portfolioValue: number
  cash: number
  cashPct: number | null
  positionPct: number | null
  nominalPct: number | null
  deltaPct: number | null
  ncPct: number | null
  // Individual position details for this account
  positions: {
    symbol: string
    quantity: number
    positionPct: number | null
    nominalPct: number | null
    deltaPct: number | null
    ncPct: number | null
    // Raw values for per-unit calculations
    value: number
    nominalExposure: number
    deltaExposure: number
    ncExposure: number
  }[]
}

interface OrderItem {
  accountNumber: string
  security: string
  action: 'Buy' | 'Sell'
  quantity: number
}

type ExposureType = 'position' | 'nominal' | 'delta' | 'nc'

const exposureLabels: Record<ExposureType, string> = {
  position: 'Position %',
  nominal: 'Nominal Exposure %',
  delta: 'Delta Adj Exposure %',
  nc: 'NC Exposure %'
}

export default function BuySell() {
  const { underlying } = useParams<{ underlying: string }>()
  const [searchParams] = useSearchParams()
  const uploadId = parseInt(searchParams.get('uploadId') || '0')
  const returnTab = searchParams.get('returnTab')
  const navigate = useNavigate()

  // UI state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [selectedSecurities, setSelectedSecurities] = useState<string[]>([])
  const [securitiesOrder, setSecuritiesOrder] = useState<string[]>([])
  const [targetValues, setTargetValues] = useState<Record<ExposureType, string>>({
    position: '',
    nominal: '',
    delta: '',
    nc: ''
  })
  const [activeTarget, setActiveTarget] = useState<ExposureType | null>(null)
  const [calculatedOrders, setCalculatedOrders] = useState<OrderItem[]>([])
  const [projectedExposures, setProjectedExposures] = useState<Record<string, AccountExposure> | null>(null)
  const [calcStatus, setCalcStatus] = useState<string | null>(null)
  // Collapsed sections state
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  // NC Spendable Cash expanded state (for collapsible breakdown)
  const [ncSpendableExpanded, setNcSpendableExpanded] = useState<Set<string>>(new Set())
  const [ncArtificialExpanded, setNcArtificialExpanded] = useState<Set<string>>(new Set())
  // Allow short per security (default: all allowed)
  const [allowShort, setAllowShort] = useState<Record<string, boolean>>({})
  // Drag and drop state
  const [draggedId, setDraggedId] = useState<string | null>(null)
  // Selected accounts for calculations (default: all included accounts)
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<number> | null>(null)
  // Custom prices for securities (keyed by security id)
  // customPrices: raw text shown in input fields (updates as user types)
  // appliedCustomPrices: parsed numbers used for calculations (updates when Calculate Orders is clicked)
  const [customPrices, setCustomPrices] = useState<Record<string, string>>({})
  const [appliedCustomPrices, setAppliedCustomPrices] = useState<Record<string, number | null>>({})
  // Currency for custom prices - always uses security's native currency (no user selection)
  // appliedCustomPriceCurrencies is kept for fallback logic (always empty, falls back to sec.currency)
  const [appliedCustomPriceCurrencies] = useState<Record<string, string | null>>({})
  // Trigger for running calculation (incremented when Calculate Orders is clicked)
  const [calcTrigger, setCalcTrigger] = useState(0)

  // Fetch data
  const { data: accountsData } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.getAccounts(),
  })

  // Fetch all account portfolios
  const includedAccounts = useMemo(() => {
    return accountsData?.accounts.filter(a => !a.exclude_from_aggregate) || []
  }, [accountsData])

  const accountIds = useMemo(() => includedAccounts.map(a => a.id), [includedAccounts])

  // Initialize selected accounts when data loads (default: all accounts selected)
  useEffect(() => {
    if (includedAccounts.length > 0 && selectedAccountIds === null) {
      setSelectedAccountIds(new Set(includedAccounts.map(a => a.id)))
    }
  }, [includedAccounts, selectedAccountIds])

  // Toggle account selection for calculations
  const toggleAccountSelection = useCallback((accountId: number) => {
    setSelectedAccountIds(prev => {
      const next = new Set(prev || [])
      if (next.has(accountId)) {
        next.delete(accountId)
      } else {
        next.add(accountId)
      }
      return next
    })
    // Clear calculated orders when selection changes
    setCalculatedOrders([])
    setProjectedExposures(null)
    setCalcStatus(null)
  }, [])

  const accountPortfolioQueries = useQuery({
    queryKey: ['allPortfolios', uploadId, accountIds],
    queryFn: async () => {
      const results: Record<number, { positions: OpenPosition[]; portfolioValue: number; cash: number }> = {}
      for (const account of includedAccounts) {
        const portfolio = await api.getPortfolio(uploadId, account.id)
        // Get portfolio value from NAV total row with calculated_total_usd
        // Note: calculated_total_usd is stored in row_type='Data', asset_class='Total'
        const navTotal = portfolio.net_asset_values.find(
          nav => nav.row_type === 'Data' && nav.asset_class === 'Total'
        )
        const portfolioValue = Number(navTotal?.calculated_total_usd || 0) ||
          portfolio.open_positions
            .filter(p => p.row_type === 'Data')
            .reduce((sum, p) => sum + Number(p.value || 0), 0)
        // Get cash from Ending Cash line in Base Currency Summary
        const endingCashRow = portfolio.cash_reports.find(
          cash => cash.line_item === 'Ending Cash' && cash.currency === 'Base Currency Summary'
        )
        const cash = Number(endingCashRow?.total) || 0
        results[account.id] = {
          positions: portfolio.open_positions,
          portfolioValue,
          cash,
        }
      }
      return results
    },
    enabled: !!uploadId && includedAccounts.length > 0,
  })

  // Prefetch individual account portfolios to keep cache warm for Portfolio page
  // Uses same query keys as Portfolio.tsx so cache is shared
  useQueries({
    queries: includedAccounts.map(account => ({
      queryKey: ['portfolio', uploadId, account.id],
      queryFn: () => api.getPortfolio(Number(uploadId), account.id),
      enabled: !!uploadId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Fetch options for this underlying to get underlying_price from options table
  const { data: optionsData } = useQuery({
    queryKey: ['options', underlying],
    queryFn: () => api.getOptions({ underlying }),
    enabled: !!underlying,
  })

  // Fetch FX rates for currency conversion
  const { data: fxRatesData } = useQuery({
    queryKey: ['fxRates'],
    queryFn: () => api.getFxRates('USD'),
  })

  // Create FX rate lookup (USD per 1 unit of foreign currency)
  const fxRates = useMemo((): Record<string, number> => {
    const rates: Record<string, number> = { USD: 1 }
    if (fxRatesData) {
      for (const rate of fxRatesData) {
        // rate.rate is how many quote_currency per 1 base_currency (USD)
        // We want: how many USD per 1 unit of quote_currency
        // So we invert: 1 / rate.rate
        rates[rate.quote_currency] = 1 / rate.rate
      }
    }
    return rates
  }, [fxRatesData])

  // Build account exposure data for the underlying
  const accountExposures = useMemo((): AccountExposure[] => {
    if (!accountPortfolioQueries.data || !includedAccounts.length) return []

    return includedAccounts.map(account => {
      const accountData = accountPortfolioQueries.data[account.id]
      if (!accountData) {
        return {
          accountNumber: account.account_number,
          accountId: account.id,
          portfolioValue: 0,
          cash: 0,
          cashPct: null,
          positionPct: null,
          nominalPct: null,
          deltaPct: null,
          ncPct: null,
          positions: [],
        }
      }

      const { positions, portfolioValue, cash } = accountData
      const cashPct = portfolioValue > 0 ? cash / portfolioValue : null

      // Filter positions for this underlying
      const relevantPositions = positions.filter(p => {
        if (p.row_type !== 'Data' || !p.symbol) return false
        if (p.asset_category === 'Stocks' && p.symbol === underlying) return true
        if (p.asset_category === 'Equity and Index Options') {
          const parsed = parseOptionSymbol(p.symbol)
          return parsed?.underlying === underlying
        }
        return false
      })

      // Calculate totals
      let positionPct = 0
      let nominalPct = 0
      let deltaPct = 0
      let ncPct = 0

      const posDetails = relevantPositions.map(p => {
        positionPct += Number(p.position_pct || 0)
        nominalPct += Number(p.nominal_exposure_pct || 0)
        deltaPct += Number(p.delta_exposure_pct || 0)
        ncPct += Number(p.nc_exposure_pct || 0)

        return {
          symbol: p.symbol || '',
          quantity: Number(p.quantity || 0),
          multiplier: Number(p.multiplier || 1),
          assetCategory: p.asset_category || '',
          positionPct: p.position_pct !== null ? Number(p.position_pct) : null,
          nominalPct: p.nominal_exposure_pct !== null ? Number(p.nominal_exposure_pct) : null,
          deltaPct: p.delta_exposure_pct !== null ? Number(p.delta_exposure_pct) : null,
          ncPct: p.nc_exposure_pct !== null ? Number(p.nc_exposure_pct) : null,
          // Store raw values for per-unit calculation
          value: Number(p.value || 0),
          nominalExposure: Number(p.nominal_exposure || 0),
          deltaExposure: Number(p.delta_adjusted_exposure || 0),
          ncExposure: Number(p.nc_exposure || 0),
        }
      })

      return {
        accountNumber: account.account_number,
        accountId: account.id,
        portfolioValue,
        cash,
        cashPct,
        positionPct: relevantPositions.length > 0 ? positionPct : null,
        nominalPct: relevantPositions.length > 0 ? nominalPct : null,
        deltaPct: relevantPositions.length > 0 ? deltaPct : null,
        ncPct: relevantPositions.length > 0 ? ncPct : null,
        positions: posDetails,
      }
    }).sort((a, b) => b.portfolioValue - a.portfolioValue) // Sort by value descending
  }, [accountPortfolioQueries.data, includedAccounts, underlying])

  // Filter exposures to only selected accounts (used for calculations)
  const selectedAccountExposures = useMemo(() => {
    if (!selectedAccountIds) return accountExposures
    return accountExposures.filter(exp => selectedAccountIds.has(exp.accountId))
  }, [accountExposures, selectedAccountIds])

  // Calculate NC Artificial Cash per account
  // NC artificial cash = sum of (shares_short * (underlying_price - option_price)) for each inverse covered call
  // An inverse covered call is: short stock + long call on the same underlying
  // NOTE: This calculation has limitations with foreign currencies, multiple options per underlying,
  // and short positions > 100 shares. It's designed for a specific edge case.
  interface NcArtificialCashItem {
    underlying: string
    sharesShort: number
    underlyingPrice: number
    optionPrice: number
    artificialCash: number
  }

  interface NcArtificialCashData {
    total: number
    totalPct: number | null
    items: NcArtificialCashItem[]
  }

  // Calculate NC Artificial Cash per account
  // This represents cash "locked up" by inverse covered calls (short stock + long call)
  // Formula: abs(shares_short) * (underlying_price - option_price)
  //
  // LIMITATIONS:
  // - Foreign currency positions: Uses USD values, FX conversion may cause slight inaccuracies
  // - Multiple long calls on same underlying: Uses first call found, may not match intended pairing
  // - >100 shares short: Calculation uses total shares short, not adjusted for option contracts
  const ncArtificialCashByAccount = useMemo((): Record<number, NcArtificialCashData> => {
    const result: Record<number, NcArtificialCashData> = {}

    if (!accountPortfolioQueries.data) return result

    for (const account of includedAccounts) {
      const accountData = accountPortfolioQueries.data[account.id]
      if (!accountData) {
        result[account.id] = { total: 0, totalPct: null, items: [] }
        continue
      }

      const { positions, portfolioValue } = accountData
      const items: NcArtificialCashItem[] = []

      // Group positions by underlying
      const positionsByUnderlying: Record<string, { shortStock: number; stockPrice: number; longCalls: { symbol: string; qty: number; price: number }[] }> = {}

      for (const pos of positions) {
        if (pos.row_type !== 'Data' || !pos.symbol) continue

        if (pos.asset_category === 'Stocks') {
          const qty = Number(pos.quantity || 0)
          // Use applied custom price if available, converting to USD
          const stockSecId = `stock-${pos.symbol}`
          const customStockPrice = appliedCustomPrices[stockSecId]
          let priceUsd: number
          if (customStockPrice !== null && customStockPrice !== undefined) {
            // Convert custom price to USD using the input currency
            const inputCurrency = appliedCustomPriceCurrencies[stockSecId] ?? (pos.currency || 'USD')
            priceUsd = customStockPrice * (fxRates[inputCurrency] ?? 1)
          } else {
            // Use position value / quantity to get USD price (position value is already in USD)
            priceUsd = qty !== 0 ? Math.abs(Number(pos.value || 0) / qty) : 0
          }
          if (qty < 0) {
            // Short stock
            if (!positionsByUnderlying[pos.symbol]) {
              positionsByUnderlying[pos.symbol] = { shortStock: 0, stockPrice: 0, longCalls: [] }
            }
            positionsByUnderlying[pos.symbol].shortStock = Math.abs(qty)
            positionsByUnderlying[pos.symbol].stockPrice = priceUsd
          }
        } else if (pos.asset_category === 'Equity and Index Options') {
          const parsed = parseOptionSymbol(pos.symbol)
          if (!parsed || parsed.type !== 'C') continue // Only calls

          const qty = Number(pos.quantity || 0)
          if (qty > 0) {
            // Long call
            const underlyingSymbol = parsed.underlying
            if (!positionsByUnderlying[underlyingSymbol]) {
              positionsByUnderlying[underlyingSymbol] = { shortStock: 0, stockPrice: 0, longCalls: [] }
            }
            // Use applied custom price if available, converting to USD
            const optSecId = `option-${pos.symbol}`
            const customOptPrice = appliedCustomPrices[optSecId]
            let optionPricePerShareUsd: number
            if (customOptPrice !== null && customOptPrice !== undefined) {
              const inputCurrency = appliedCustomPriceCurrencies[optSecId] ?? (pos.currency || 'USD')
              optionPricePerShareUsd = customOptPrice * (fxRates[inputCurrency] ?? 1)
            } else {
              // Use position value / quantity / 100 to get per-share USD price
              optionPricePerShareUsd = qty !== 0 ? Math.abs(Number(pos.value || 0) / qty) / 100 : 0
            }
            positionsByUnderlying[underlyingSymbol].longCalls.push({
              symbol: pos.symbol,
              qty,
              price: optionPricePerShareUsd
            })
          }
        }
      }

      // Calculate artificial cash for each underlying with both short stock and long call
      for (const [underlyingSymbol, data] of Object.entries(positionsByUnderlying)) {
        if (data.shortStock > 0 && data.longCalls.length > 0) {
          // Use the first long call (limitation: only handles one call per underlying)
          const call = data.longCalls[0]
          const artificialCash = data.shortStock * (data.stockPrice - call.price)
          items.push({
            underlying: underlyingSymbol,
            sharesShort: data.shortStock,
            underlyingPrice: data.stockPrice,
            optionPrice: call.price,
            artificialCash
          })
        }
      }

      const total = items.reduce((sum, item) => sum + item.artificialCash, 0)
      result[account.id] = {
        total,
        totalPct: portfolioValue > 0 ? total / portfolioValue : null,
        items
      }
    }

    return result
  }, [accountPortfolioQueries.data, includedAccounts, appliedCustomPrices, appliedCustomPriceCurrencies, fxRates])

  // Get underlying price - first from options table, fallback to stock position
  const underlyingPrice = useMemo((): number | null => {
    // Try options table first (most reliable source)
    if (optionsData?.options?.length) {
      const optionWithPrice = optionsData.options.find(o => o.underlying_price !== null)
      if (optionWithPrice?.underlying_price) {
        return Number(optionWithPrice.underlying_price)
      }
    }
    // Fallback to stock position if no options data
    if (accountPortfolioQueries.data) {
      for (const accountData of Object.values(accountPortfolioQueries.data)) {
        const stockPos = accountData.positions.find(
          p => p.row_type === 'Data' && p.asset_category === 'Stocks' && p.symbol === underlying
        )
        if (stockPos?.close_price) {
          return Number(stockPos.close_price)
        }
      }
    }
    return null
  }, [optionsData, accountPortfolioQueries.data, underlying])

  // Get underlying currency from position data
  const underlyingCurrency = useMemo((): string => {
    // Try to find currency from options table first
    if (optionsData?.options?.length) {
      const optionWithCurrency = optionsData.options.find(o => o.currency)
      if (optionWithCurrency?.currency) {
        return optionWithCurrency.currency
      }
    }
    // Fallback to stock position
    if (accountPortfolioQueries.data) {
      for (const accountData of Object.values(accountPortfolioQueries.data)) {
        const stockPos = accountData.positions.find(
          p => p.row_type === 'Data' && p.asset_category === 'Stocks' && p.symbol === underlying
        )
        if (stockPos?.currency) {
          return stockPos.currency
        }
      }
    }
    return 'USD' // Default to USD
  }, [optionsData, accountPortfolioQueries.data, underlying])

  // Build available securities list with per-unit contributions
  const availableSecurities = useMemo((): SecurityItem[] => {
    const securities: SecurityItem[] = []
    const seenSymbols = new Set<string>()

    // Calculate per-unit contributions for each account
    const calculatePerUnitContributions = (
      symbol: string,
      securityType: 'stock' | 'call' | 'put' | 'covered_call',
      callSymbol?: string
    ): Record<number, { position: number; nominal: number; delta: number; nc: number }> => {
      const contributions: Record<number, { position: number; nominal: number; delta: number; nc: number }> = {}

      for (const exposure of accountExposures) {
        const pv = exposure.portfolioValue
        if (pv <= 0) {
          contributions[exposure.accountId] = { position: 0, nominal: 0, delta: 0, nc: 0 }
          continue
        }

        if (securityType === 'stock') {
          // For stock: 1 share contributes to each exposure type
          const stockPos = exposure.positions.find(p => p.symbol === symbol)
          const stockSecId = `stock-${symbol}`
          const customPrice = appliedCustomPrices[stockSecId]
          const defaultPrice = underlyingPrice ?? 0

          let perUnit: { position: number; nominal: number; delta: number; nc: number }

          // If custom price is set, convert to USD and use for % calculations
          if (customPrice !== null && customPrice !== undefined) {
            const inputCurrency = appliedCustomPriceCurrencies[stockSecId] ?? underlyingCurrency
            const fxRate = fxRates[inputCurrency] ?? 1
            const customPriceUsd = customPrice * fxRate
            const pricePerUnit = customPriceUsd / pv
            perUnit = {
              position: pricePerUnit,
              nominal: pricePerUnit,
              delta: pricePerUnit,
              nc: pricePerUnit,
            }
          } else if (stockPos && stockPos.quantity !== 0 && stockPos.positionPct !== null) {
            // No custom price: derive from existing position data
            const qty = Math.abs(stockPos.quantity)
            perUnit = {
              position: Math.abs(stockPos.positionPct) / qty,
              nominal: stockPos.nominalPct !== null ? Math.abs(stockPos.nominalPct) / qty : 0,
              delta: stockPos.deltaPct !== null ? Math.abs(stockPos.deltaPct) / qty : 0,
              nc: stockPos.ncPct !== null ? Math.abs(stockPos.ncPct) / qty : 0,
            }
          } else {
            // Fallback: no existing position, use default price converted to USD / portfolioValue
            const fxRate = fxRates[underlyingCurrency] ?? 1
            const defaultPriceUsd = defaultPrice * fxRate
            const fallbackPerUnit = defaultPriceUsd / pv
            perUnit = {
              position: fallbackPerUnit,
              nominal: fallbackPerUnit,
              delta: fallbackPerUnit,
              nc: fallbackPerUnit,
            }
          }

          contributions[exposure.accountId] = perUnit
        } else if (securityType === 'covered_call') {
          // Covered call = 100 shares of stock + sell 1 call
          // Check if there's an applied custom price for this covered call itself
          const ccSecId = `covered-${callSymbol}`
          const ccCustomPrice = appliedCustomPrices[ccSecId]

          // If covered call has its own custom price, convert to USD and use directly
          // The custom price is "per share equivalent" (stock - call premium)
          // Buying 1 CC = buying 100 share equivalents
          if (ccCustomPrice !== null && ccCustomPrice !== undefined) {
            const inputCurrency = appliedCustomPriceCurrencies[ccSecId] ?? underlyingCurrency
            const fxRate = fxRates[inputCurrency] ?? 1
            const ccCustomPriceUsd = ccCustomPrice * fxRate
            const ccValue = ccCustomPriceUsd * 100 // Total value of 1 covered call contract
            const perUnit = ccValue / pv
            contributions[exposure.accountId] = {
              position: perUnit,
              nominal: perUnit,
              delta: perUnit,
              nc: perUnit,
            }
            continue
          }

          // No custom CC price - fall back to calculating from stock + call components
          const stockPos = exposure.positions.find(p => p.symbol === underlying)
          // Use applied custom price (converted to USD) if set, otherwise underlying price
          const stockSecId = `stock-${underlying}`
          const stockCustomPrice = appliedCustomPrices[stockSecId]
          let stockPrice: number
          if (stockCustomPrice !== null && stockCustomPrice !== undefined) {
            const inputCurrency = appliedCustomPriceCurrencies[stockSecId] ?? underlyingCurrency
            const fxRate = fxRates[inputCurrency] ?? 1
            stockPrice = stockCustomPrice * fxRate
          } else {
            stockPrice = underlyingPrice ?? 0
          }

          let stockPerUnit: { position: number; nominal: number; delta: number; nc: number }
          if (stockPos && stockPos.quantity !== 0 && stockPos.positionPct !== null) {
            const qty = Math.abs(stockPos.quantity)
            stockPerUnit = {
              position: Math.abs(stockPos.positionPct) / qty,
              nominal: stockPos.nominalPct !== null ? Math.abs(stockPos.nominalPct) / qty : 0,
              delta: stockPos.deltaPct !== null ? Math.abs(stockPos.deltaPct) / qty : 0,
              nc: stockPos.ncPct !== null ? Math.abs(stockPos.ncPct) / qty : 0,
            }
          } else {
            // Fallback to price/pv
            const perUnit = stockPrice / pv
            stockPerUnit = { position: perUnit, nominal: perUnit, delta: perUnit, nc: perUnit }
          }

          // Get call contribution from existing position data using pct/qty
          const callPos = exposure.positions.find(p => p.symbol === callSymbol)
          let callContributions = { position: 0, nominal: 0, delta: 0, nc: 0 }
          if (callPos && callPos.quantity !== 0 && callPos.positionPct !== null) {
            const qty = Math.abs(callPos.quantity)
            callContributions = {
              position: Math.abs(callPos.positionPct) / qty,
              nominal: callPos.nominalPct !== null ? Math.abs(callPos.nominalPct) / qty : 0,
              delta: callPos.deltaPct !== null ? Math.abs(callPos.deltaPct) / qty : 0,
              nc: callPos.ncPct !== null ? Math.abs(callPos.ncPct) / qty : 0,
            }
          }
          // If no existing position, we can't estimate - contributions stay at 0

          // Covered call: buy 100 stock, sell 1 call (so subtract call contribution)
          const ccContribution = {
            position: 100 * stockPerUnit.position - callContributions.position,
            nominal: 100 * stockPerUnit.nominal - callContributions.nominal,
            delta: 100 * stockPerUnit.delta - callContributions.delta,
            nc: 100 * stockPerUnit.nc - callContributions.nc,
          }
          contributions[exposure.accountId] = ccContribution
        } else {
          // For options: check for applied custom price first
          const optionSecId = `option-${symbol}`
          const optionCustomPrice = appliedCustomPrices[optionSecId]

          // If option has a custom price, convert to USD and use
          // Option value = price * 100 (multiplier)
          if (optionCustomPrice !== null && optionCustomPrice !== undefined) {
            const inputCurrency = appliedCustomPriceCurrencies[optionSecId] ?? underlyingCurrency
            const fxRate = fxRates[inputCurrency] ?? 1
            const optionCustomPriceUsd = optionCustomPrice * fxRate
            const optionValue = optionCustomPriceUsd * 100 // 1 contract = 100 shares
            const perUnit = optionValue / pv
            contributions[exposure.accountId] = {
              position: perUnit,
              nominal: perUnit,
              delta: perUnit,
              nc: perUnit,
            }
            continue
          }

          // No custom price - derive from existing position data using pct/qty
          // perUnit represents "contribution per contract if you BUY 1"
          // sign flips for short positions so buying (closing short) gives correct direction
          const pos = exposure.positions.find(p => p.symbol === symbol)
          if (pos && pos.quantity !== 0 && pos.positionPct !== null) {
            const qty = Math.abs(pos.quantity)
            const sign = pos.quantity > 0 ? 1 : -1
            contributions[exposure.accountId] = {
              position: (sign * pos.positionPct) / qty,
              nominal: pos.nominalPct !== null ? (sign * pos.nominalPct) / qty : 0,
              delta: pos.deltaPct !== null ? (sign * pos.deltaPct) / qty : 0,
              nc: pos.ncPct !== null ? (sign * pos.ncPct) / qty : 0,
            }
          } else {
            // No existing position - can't calculate contribution
            contributions[exposure.accountId] = { position: 0, nominal: 0, delta: 0, nc: 0 }
          }
        }
      }

      return contributions
    }

    // Add stock
    securities.push({
      id: `stock-${underlying}`,
      symbol: underlying || '',
      type: 'stock',
      displayName: `${underlying} Stock`,
      price: underlyingPrice,
      currency: underlyingCurrency,
      perUnitContributions: calculatePerUnitContributions(underlying || '', 'stock'),
    })

    // Add options from positions across all accounts
    if (accountPortfolioQueries.data) {
      for (const accountData of Object.values(accountPortfolioQueries.data)) {
        for (const pos of accountData.positions) {
          if (pos.row_type !== 'Data' || !pos.symbol) continue
          if (pos.asset_category !== 'Equity and Index Options') continue

          const parsed = parseOptionSymbol(pos.symbol)
          if (!parsed || parsed.underlying !== underlying) continue
          if (seenSymbols.has(pos.symbol)) continue

          seenSymbols.add(pos.symbol)

          const isCall = parsed.type === 'C'

          securities.push({
            id: `option-${pos.symbol}`,
            symbol: pos.symbol,
            type: isCall ? 'call' : 'put',
            displayName: `${parsed.underlying} ${parsed.expiration.toLocaleDateString()} $${parsed.strike} ${isCall ? 'Call' : 'Put'}`,
            expiration: parsed.expiration,
            strike: parsed.strike,
            price: pos.close_price !== null ? Number(pos.close_price) : null,
            currency: pos.currency || underlyingCurrency,
            perUnitContributions: calculatePerUnitContributions(pos.symbol, isCall ? 'call' : 'put'),
          })
        }
      }
    }

    // Add covered call combinations (stock + short call at 100:1)
    const callOptions = securities.filter(s => s.type === 'call')
    const stockSec = securities.find(s => s.type === 'stock')
    for (const call of callOptions) {
      // Covered call price = stock price - call price (per share equivalent)
      const stockPrice = stockSec?.price ?? underlyingPrice ?? 0
      const callPrice = call.price ?? 0
      const coveredCallPrice = typeof stockPrice === 'number' && typeof callPrice === 'number'
        ? stockPrice - callPrice
        : null
      securities.push({
        id: `covered-${call.symbol}`,
        symbol: `CC:${underlying}+${call.symbol}`,
        type: 'covered_call',
        displayName: `+ (100 ${underlying}) - (1 ${call.displayName})`,
        expiration: call.expiration,
        strike: call.strike,
        callSymbol: call.symbol,
        price: coveredCallPrice,
        currency: call.currency,
        perUnitContributions: calculatePerUnitContributions(call.symbol, 'covered_call', call.symbol),
      })
    }

    return sortSecurities(securities)
  }, [underlying, accountPortfolioQueries.data, accountExposures, underlyingPrice, underlyingCurrency, appliedCustomPrices, appliedCustomPriceCurrencies, fxRates])

  // Initialize securities selection and order (only on first load)
  // Logic: If long stock + short call exists, select covered calls only
  //        Otherwise, select only securities with existing positions
  const initializedRef = useRef(false)
  useEffect(() => {
    if (!initializedRef.current && availableSecurities.length > 0 && accountExposures.length > 0) {
      // Collect all positions across accounts
      const allPositions = accountExposures.flatMap(exp => exp.positions)

      // Check for long stock
      const hasLongStock = allPositions.some(p =>
        p.symbol === underlying && p.quantity > 0
      )

      // Check for short calls (negative quantity on call options)
      const hasShortCall = availableSecurities.some(sec =>
        sec.type === 'call' &&
        allPositions.some(p => p.symbol === sec.symbol && p.quantity < 0)
      )

      let selectedIds: string[]

      if (hasLongStock && hasShortCall) {
        // Covered call pattern: select only covered calls
        selectedIds = availableSecurities
          .filter(s => s.type === 'covered_call')
          .map(s => s.id)
      } else {
        // Select only securities with existing positions
        const symbolsWithPositions = new Set(
          allPositions
            .filter(p => p.quantity !== 0)
            .map(p => p.symbol)
        )
        selectedIds = availableSecurities
          .filter(s => s.type !== 'covered_call' && symbolsWithPositions.has(s.symbol))
          .map(s => s.id)
      }

      // If nothing would be selected, fall back to all non-covered-call securities
      if (selectedIds.length === 0) {
        selectedIds = availableSecurities
          .filter(s => s.type !== 'covered_call')
          .map(s => s.id)
      }

      setSelectedSecurities(selectedIds)
      setSecuritiesOrder(availableSecurities.map(s => s.id))
      // Initialize allowShort - default all to false (no shorting)
      const shortDefaults: Record<string, boolean> = {}
      availableSecurities.forEach(s => { shortDefaults[s.id] = false })
      setAllowShort(shortDefaults)
      initializedRef.current = true
    }
  }, [availableSecurities, accountExposures, underlying])

  // Toggle row expansion
  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Toggle security selection
  const toggleSecurity = (id: string) => {
    setSelectedSecurities(prev => {
      if (prev.includes(id)) {
        return prev.filter(s => s !== id)
      }
      return [...prev, id]
    })
  }

  // Drag and drop handlers for reordering securities
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!draggedId || draggedId === targetId) return

    setSecuritiesOrder(prev => {
      const dragIdx = prev.indexOf(draggedId)
      const targetIdx = prev.indexOf(targetId)
      if (dragIdx === -1 || targetIdx === -1) return prev

      const newOrder = [...prev]
      newOrder.splice(dragIdx, 1)
      newOrder.splice(targetIdx, 0, draggedId)
      return newOrder
    })
  }

  const handleDragEnd = () => {
    setDraggedId(null)
  }

  // Handle target input change - only one field active at a time
  const handleTargetChange = (type: ExposureType, value: string) => {
    // Clear all other fields when typing in this one
    setTargetValues({
      position: type === 'position' ? value : '',
      nominal: type === 'nominal' ? value : '',
      delta: type === 'delta' ? value : '',
      nc: type === 'nc' ? value : '',
    })
  }

  // Handle calculate button or enter key - just sets state, actual calculation happens in useEffect
  const handleCalculate = useCallback((triggeredType?: ExposureType) => {
    setCalcStatus(null)

    // Determine which target to use
    let targetType: ExposureType | null = triggeredType || null

    if (!targetType) {
      // Find the first non-empty target
      const types: ExposureType[] = ['position', 'nominal', 'delta', 'nc']
      for (const t of types) {
        if (targetValues[t].trim() !== '') {
          targetType = t
          break
        }
      }
    }

    if (!targetType || targetValues[targetType].trim() === '') {
      setCalculatedOrders([])
      setProjectedExposures(null)
      setActiveTarget(null)
      setCalcStatus('Enter a target percentage to calculate orders')
      return
    }

    const targetPct = parseFloat(targetValues[targetType]) / 100 // Convert from percent to decimal
    if (isNaN(targetPct)) {
      setCalculatedOrders([])
      setProjectedExposures(null)
      setCalcStatus('Invalid target percentage')
      return
    }

    setActiveTarget(targetType)

    // Clear other target values
    setTargetValues(prev => {
      const newValues = { position: '', nominal: '', delta: '', nc: '' }
      newValues[targetType!] = prev[targetType!]
      return newValues
    })

    // Apply custom prices and trigger calculation
    // Parse text inputs to numbers for calculations
    const parsedPrices: Record<string, number | null> = {}
    for (const [id, text] of Object.entries(customPrices)) {
      const num = parseFloat(text)
      parsedPrices[id] = isNaN(num) ? null : num
    }
    setAppliedCustomPrices(parsedPrices)
    setCalcTrigger(prev => prev + 1)
  }, [targetValues, customPrices])

  // Run order calculation after state updates (when calcTrigger changes)
  useEffect(() => {
    if (calcTrigger === 0) return // Skip initial render
    if (!activeTarget) return

    // Compute target values from state
    const targetType = activeTarget
    const targetPct = parseFloat(targetValues[activeTarget]) / 100
    if (isNaN(targetPct)) return

    // Get ordered selected securities
    const orderedSecurities = securitiesOrder
      .filter(id => selectedSecurities.includes(id))
      .map(id => availableSecurities.find(s => s.id === id)!)
      .filter(Boolean)

    // Calculate orders for each account
    const orders: OrderItem[] = []
    const newProjectedExposures: Record<string, AccountExposure> = {}
    let cashLimitedAccounts: string[] = []

    console.log('=== ORDER CALCULATION DEBUG ===')
    console.log('Target type:', targetType, 'Target pct:', targetPct)
    console.log('Underlying price:', underlyingPrice)
    console.log('Ordered securities:', orderedSecurities.map(s => ({ id: s.id, symbol: s.symbol, type: s.type })))

    // Helper function to convert a custom price to USD
    // Returns the price in USD, or null if no custom price is set
    const getCustomPriceInUsd = (securityId: string, security: SecurityItem): number | null => {
      const customPrice = appliedCustomPrices[securityId]
      if (customPrice === null || customPrice === undefined) return null

      // Get the currency the user entered the price in
      const inputCurrency = appliedCustomPriceCurrencies[securityId] ?? security.currency

      // Convert to USD
      const fxRate = fxRates[inputCurrency] ?? 1
      return customPrice * fxRate
    }

    // Helper function to calculate USD cost for a buy order
    // Uses applied custom prices (converted to USD) if set, otherwise position's actual price
    const calculateOrderCost = (security: SecurityItem, quantity: number, positions: AccountExposure['positions']): number => {
      if (security.type === 'covered_call') {
        // Check for CC's own custom price first
        const ccSecId = `covered-${security.callSymbol}`
        const ccCustomPriceUsd = getCustomPriceInUsd(ccSecId, security)
        if (ccCustomPriceUsd !== null) {
          return quantity * 100 * ccCustomPriceUsd // CC custom price is per share equivalent
        }

        // Fall back to component prices
        const stockSecId = `stock-${underlying}`
        const stockSec = availableSecurities.find(s => s.id === stockSecId)
        const stockCustomPriceUsd = stockSec ? getCustomPriceInUsd(stockSecId, stockSec) : null
        const stockPos = positions.find(p => p.symbol === underlying)
        const stockFxRate = fxRates[security.currency] ?? 1
        const stockPrice = stockCustomPriceUsd ?? (stockPos && stockPos.quantity !== 0
          ? (stockPos.value / Math.abs(stockPos.quantity)) * stockFxRate  // value is native currency, convert to USD
          : (underlyingPrice || 0) * stockFxRate)

        const callSecId = `option-${security.callSymbol}`
        const callSec = availableSecurities.find(s => s.id === callSecId)
        const callCustomPriceUsd = callSec ? getCustomPriceInUsd(callSecId, callSec) : null
        const callPos = positions.find(p => p.symbol === security.callSymbol)
        const callPrice = callCustomPriceUsd ?? (callPos && callPos.quantity !== 0
          ? (Math.abs(callPos.value / callPos.quantity) / 100) * stockFxRate  // value is native currency, convert to USD
          : (security.price || 0) * stockFxRate)

        return quantity * 100 * (stockPrice - callPrice)
      } else if (security.type === 'call' || security.type === 'put') {
        const customPriceUsd = getCustomPriceInUsd(security.id, security)
        if (customPriceUsd !== null) {
          return quantity * customPriceUsd * 100
        }
        const optPos = positions.find(p => p.symbol === security.symbol)
        const secFxRate = fxRates[security.currency] ?? 1
        const pricePerContract = optPos && optPos.quantity !== 0
          ? Math.abs(optPos.value / optPos.quantity) * secFxRate  // value is native currency, convert to USD
          : (security.price || 0) * secFxRate * 100
        return quantity * pricePerContract
      } else {
        // Stock
        const customPriceUsd = getCustomPriceInUsd(security.id, security)
        if (customPriceUsd !== null) {
          return quantity * customPriceUsd
        }
        const stockPos = positions.find(p => p.symbol === security.symbol)
        const secFxRate = fxRates[security.currency] ?? 1
        const pricePerShare = stockPos && stockPos.quantity !== 0
          ? (stockPos.value / Math.abs(stockPos.quantity)) * secFxRate  // value is native currency, convert to USD
          : (security.price || underlyingPrice || 0) * secFxRate
        return quantity * pricePerShare
      }
    }

    for (const exposure of selectedAccountExposures) {
      console.log(`\n--- Account: ${exposure.accountNumber} ---`)
      console.log('Portfolio value:', exposure.portfolioValue)
      console.log('Cash:', exposure.cash)
      console.log('Positions:', exposure.positions.map(p => ({
        symbol: p.symbol,
        qty: p.quantity,
        value: p.value,
        positionPct: p.positionPct
      })))

      // Determine current exposure based on target type
      let currentPct: number
      switch (targetType) {
        case 'position':
          currentPct = exposure.positionPct || 0
          break
        case 'nominal':
          currentPct = exposure.nominalPct || 0
          break
        case 'delta':
          currentPct = exposure.deltaPct || 0
          break
        case 'nc':
          currentPct = exposure.ncPct || 0
          break
      }

      console.log('Current pct:', currentPct, '(', (currentPct * 100).toFixed(4), '%)')

      let remainingGap = targetPct - currentPct
      // remainingGap > 0 means we need MORE exposure
      // remainingGap < 0 means we need LESS exposure

      console.log('Remaining gap:', remainingGap, '(target - current)')

      // Track adjusted exposures for all types (for accurate projections)
      const projectedExposures = {
        position: exposure.positionPct || 0,
        nominal: exposure.nominalPct || 0,
        delta: exposure.deltaPct || 0,
        nc: exposure.ncPct || 0,
      }

      // Track remaining cash for this account (used to constrain buy orders)
      let remainingCash = exposure.cash

      // Get account settings for shorting and minimum cash
      const account = includedAccounts.find(a => a.id === exposure.accountId)
      const accountAllowsShort = account?.can_go_short ?? true

      // Calculate minimum cash floor from account's min_cash_pct setting
      const minCashPct = account?.min_cash_pct ?? 0
      const minCashFloor = exposure.portfolioValue * (minCashPct / 100)

      // Get NC artificial cash for this account (reserved for inverse covered calls)
      const ncArtificialCash = ncArtificialCashByAccount[exposure.accountId]?.total ?? 0

      // Iterate through securities in order
      for (const security of orderedSecurities) {
        if (Math.abs(remainingGap) < 0.0001) break // Close enough

        // Get per-unit contribution for this account and exposure type
        const contributions = security.perUnitContributions[exposure.accountId]
        if (!contributions) continue

        const perUnitContribution = contributions[targetType]
        if (!perUnitContribution || !isFinite(perUnitContribution)) continue // Skip if no valid contribution

        // Find existing position for this security in this account
        let currentQty = 0
        if (security.type === 'covered_call') {
          // Calculate covered call units from stock + short call positions
          const stockPos = exposure.positions.find(p => p.symbol === underlying)
          const stockQty = Number(stockPos?.quantity || 0)

          // Find short call position (negative quantity means short)
          const callPos = exposure.positions.find(p => p.symbol === security.callSymbol)
          const callQty = Number(callPos?.quantity || 0)
          const shortCallQty = callQty < 0 ? Math.abs(callQty) : 0

          // Covered call units = min(shares/100, short calls) - only if we have BOTH
          if (stockQty > 0 && shortCallQty > 0) {
            currentQty = Math.min(Math.floor(stockQty / 100), shortCallQty)
          }
        } else {
          const position = exposure.positions.find(p => p.symbol === security.symbol)
          currentQty = Number(position?.quantity || 0)
        }

        console.log(`  Security: ${security.symbol} (${security.type})`)
        console.log(`    currentQty: ${currentQty}, perUnitContribution: ${perUnitContribution}`)

        // Determine action: does buying or selling this security move us closer to target?
        // perUnitContribution > 0: buying INCREASES exposure, selling DECREASES
        // perUnitContribution < 0: buying DECREASES exposure, selling INCREASES
        const securityAllowsShort = allowShort[security.id] ?? false
        const canShort = securityAllowsShort && accountAllowsShort

        let action: 'Buy' | 'Sell' | null = null
        let quantity = 0

        if (remainingGap > 0) {
          // Need MORE exposure
          if (perUnitContribution > 0) {
            // Buying increases exposure → BUY
            const rawUnits = remainingGap / perUnitContribution
            const rounded = Math.round(rawUnits)
            quantity = Math.abs(rawUnits - rounded) < 0.001 ? rounded : Math.floor(rawUnits)
            if (quantity > 0) {
              // Cash constraint: limit buy quantity to what we can afford
              // Reserve min cash floor + NC artificial cash (for inverse covered calls)
              const costPerUnit = calculateOrderCost(security, 1, exposure.positions)
              const availableCash = remainingCash - minCashFloor - ncArtificialCash
              if (costPerUnit > 0 && availableCash > 0) {
                const maxAffordable = Math.floor(availableCash / costPerUnit)
                if (quantity > maxAffordable) {
                  console.log(`    Cash constraint: wanted ${quantity}, can afford ${maxAffordable} (cost/unit: $${costPerUnit.toFixed(2)}, available: $${availableCash.toFixed(2)}, floor: $${minCashFloor.toFixed(2)}, ncArtificial: $${ncArtificialCash.toFixed(2)})`)
                  quantity = maxAffordable
                  if (!cashLimitedAccounts.includes(exposure.accountNumber)) {
                    cashLimitedAccounts.push(exposure.accountNumber)
                  }
                }
              } else if (costPerUnit > 0 && availableCash <= 0) {
                console.log(`    Cash constraint: no cash available above floor, skipping buy`)
                quantity = 0
                if (!cashLimitedAccounts.includes(exposure.accountNumber)) {
                  cashLimitedAccounts.push(exposure.accountNumber)
                }
              }
              if (quantity > 0) action = 'Buy'
            }
          } else {
            // perUnitContribution < 0: buying decreases, so SELLING increases
            // This would be selling a short position (going more short adds negative exposure... wait, that decreases)
            // Actually: if contrib < 0, selling removes negative = increases exposure
            // But we can only sell if we have it or can short
            if (currentQty > 0 || canShort) {
              const rawUnits = remainingGap / Math.abs(perUnitContribution)
              const rounded = Math.round(rawUnits)
              const units = Math.abs(rawUnits - rounded) < 0.001 ? rounded : Math.ceil(rawUnits)
              quantity = canShort ? units : Math.min(currentQty, units)
              if (quantity > 0) action = 'Sell'
            }
          }
        } else if (remainingGap < 0) {
          // Need LESS exposure
          if (perUnitContribution > 0) {
            // Selling decreases exposure → SELL (if we have it or can short)
            if (currentQty > 0 || canShort) {
              const rawUnits = Math.abs(remainingGap) / perUnitContribution
              const rounded = Math.round(rawUnits)
              const units = Math.abs(rawUnits - rounded) < 0.001 ? rounded : Math.ceil(rawUnits)
              quantity = canShort ? units : Math.min(currentQty, units)
              if (quantity > 0) action = 'Sell'
            }
          } else {
            // perUnitContribution < 0: buying decreases exposure → BUY
            const rawUnits = Math.abs(remainingGap) / Math.abs(perUnitContribution)
            const rounded = Math.round(rawUnits)
            quantity = Math.abs(rawUnits - rounded) < 0.001 ? rounded : Math.floor(rawUnits)
            if (quantity > 0) {
              // Cash constraint: limit buy quantity to what we can afford
              // Reserve min cash floor + NC artificial cash (for inverse covered calls)
              const costPerUnit = calculateOrderCost(security, 1, exposure.positions)
              const availableCash = remainingCash - minCashFloor - ncArtificialCash
              if (costPerUnit > 0 && availableCash > 0) {
                const maxAffordable = Math.floor(availableCash / costPerUnit)
                if (quantity > maxAffordable) {
                  console.log(`    Cash constraint: wanted ${quantity}, can afford ${maxAffordable} (cost/unit: $${costPerUnit.toFixed(2)}, available: $${availableCash.toFixed(2)}, floor: $${minCashFloor.toFixed(2)}, ncArtificial: $${ncArtificialCash.toFixed(2)})`)
                  quantity = maxAffordable
                  if (!cashLimitedAccounts.includes(exposure.accountNumber)) {
                    cashLimitedAccounts.push(exposure.accountNumber)
                  }
                }
              } else if (costPerUnit > 0 && availableCash <= 0) {
                console.log(`    Cash constraint: no cash available above floor, skipping buy`)
                quantity = 0
                if (!cashLimitedAccounts.includes(exposure.accountNumber)) {
                  cashLimitedAccounts.push(exposure.accountNumber)
                }
              }
              if (quantity > 0) action = 'Buy'
            }
          }
        }

        console.log(`    Action: ${action}, Quantity: ${quantity}`)

        // Generate orders
        if (action && quantity > 0) {
          if (security.type === 'covered_call') {
            // Show covered calls as single line items (Buy or Unwind)
            orders.push({
              accountNumber: exposure.accountNumber,
              security: security.displayName,
              action: action === 'Buy' ? 'Buy' : 'Sell',
              quantity: quantity,
            })
          } else {
            orders.push({
              accountNumber: exposure.accountNumber,
              security: security.symbol,
              action: action,
              quantity: quantity,
            })
          }

          // Update projected exposures
          const multiplier = action === 'Buy' ? 1 : -1
          projectedExposures.position += multiplier * quantity * contributions.position
          projectedExposures.nominal += multiplier * quantity * contributions.nominal
          projectedExposures.delta += multiplier * quantity * contributions.delta
          projectedExposures.nc += multiplier * quantity * contributions.nc

          // Update remaining cash (buy decreases, sell increases)
          const orderCost = calculateOrderCost(security, quantity, exposure.positions)
          if (action === 'Buy') {
            remainingCash -= orderCost
          } else {
            remainingCash += orderCost
          }
          console.log(`    Cash after order: $${remainingCash.toFixed(2)}`)

          remainingGap -= multiplier * quantity * perUnitContribution
        }
      }

      // Store projected exposure with all updated values
      newProjectedExposures[exposure.accountNumber] = {
        ...exposure,
        positionPct: projectedExposures.position,
        nominalPct: projectedExposures.nominal,
        deltaPct: projectedExposures.delta,
        ncPct: projectedExposures.nc,
      }
    }

    setCalculatedOrders(orders)
    setProjectedExposures(newProjectedExposures)

    // Set status message if no orders
    if (orders.length === 0) {
      // Diagnose why
      const allAtTarget = selectedAccountExposures.every(exp => {
        let currentPct: number
        switch (targetType) {
          case 'position': currentPct = exp.positionPct || 0; break
          case 'nominal': currentPct = exp.nominalPct || 0; break
          case 'delta': currentPct = exp.deltaPct || 0; break
          case 'nc': currentPct = exp.ncPct || 0; break
        }
        return Math.abs(currentPct - targetPct) < 0.0001
      })

      if (allAtTarget) {
        setCalcStatus('All accounts already at target')
      } else if (underlyingPrice === null) {
        setCalcStatus('No underlying price available - cannot calculate orders')
      } else {
        // Check if all per-unit contributions are 0
        const hasContributions = orderedSecurities.some(sec =>
          selectedAccountExposures.some(exp => {
            const contrib = sec.perUnitContributions[exp.accountId]
            return contrib && contrib[targetType!] && isFinite(contrib[targetType!])
          })
        )
        if (!hasContributions) {
          setCalcStatus('No per-unit contribution data for selected securities')
        } else if (cashLimitedAccounts.length > 0) {
          setCalcStatus(`Orders limited by available cash in: ${cashLimitedAccounts.join(', ')}`)
        } else {
          setCalcStatus('Could not generate orders - gap too small or no valid securities')
        }
      }
    } else {
      // Show info message if orders were limited by cash
      if (cashLimitedAccounts.length > 0) {
        setCalcStatus(`Some orders limited by available cash in: ${cashLimitedAccounts.join(', ')}`)
      } else {
        setCalcStatus(null)
      }
    }
  }, [calcTrigger, activeTarget, targetValues, securitiesOrder, selectedSecurities, availableSecurities, selectedAccountExposures, underlyingPrice, includedAccounts, allowShort, appliedCustomPrices, appliedCustomPriceCurrencies, fxRates, underlying, ncArtificialCashByAccount])

  // Handle key press on target inputs
  const handleKeyDown = (e: React.KeyboardEvent, type: ExposureType) => {
    if (e.key === 'Enter') {
      handleCalculate(type)
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault() // Prevent default increment/decrement behavior
      const types: ExposureType[] = ['position', 'nominal', 'delta', 'nc']
      const currentIndex = types.indexOf(type)
      let nextIndex: number
      if (e.key === 'ArrowUp') {
        nextIndex = Math.max(0, currentIndex - 1)
      } else {
        nextIndex = Math.min(types.length - 1, currentIndex + 1)
      }
      if (nextIndex !== currentIndex) {
        // Focus the next input using data attribute
        const nextInput = document.querySelector(`input[data-target-type="${types[nextIndex]}"]`) as HTMLInputElement
        nextInput?.focus()
      }
    }
  }

  // Ordered securities for display
  const orderedSecuritiesForDisplay = useMemo(() => {
    return securitiesOrder
      .map(id => availableSecurities.find(s => s.id === id))
      .filter((s): s is SecurityItem => s !== undefined)
  }, [securitiesOrder, availableSecurities])

  // Get all unique symbols across all accounts for expanded rows, sorted properly
  const allSymbols = useMemo(() => {
    const symbols = new Set<string>()
    for (const exp of accountExposures) {
      for (const pos of exp.positions) {
        symbols.add(pos.symbol)
      }
    }
    // Sort: stock first, then options by strike (lowest first), then expiration (earliest first)
    return Array.from(symbols).sort((a, b) => {
      const aOpt = parseOptionSymbol(a)
      const bOpt = parseOptionSymbol(b)
      // Stock (non-option) comes first
      if (!aOpt && bOpt) return -1
      if (aOpt && !bOpt) return 1
      if (!aOpt && !bOpt) return a.localeCompare(b)
      // Both are options - sort by strike first, then expiration
      if (aOpt!.strike !== bOpt!.strike) return aOpt!.strike - bOpt!.strike
      return aOpt!.expiration.getTime() - bOpt!.expiration.getTime()
    })
  }, [accountExposures])

  // Calculate weighted average (weighted by portfolio value) - uses selected accounts only
  const calculateWeightedAvg = useCallback((
    getValue: (exp: AccountExposure) => number | null
  ): number | null => {
    let weightedSum = 0
    let totalWeight = 0
    for (const exp of selectedAccountExposures) {
      const val = getValue(exp)
      if (val !== null && exp.portfolioValue > 0) {
        weightedSum += val * exp.portfolioValue
        totalWeight += exp.portfolioValue
      }
    }
    return totalWeight > 0 ? weightedSum / totalWeight : null
  }, [selectedAccountExposures])

  // Toggle section collapse
  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  if (!underlying) {
    return <div className="p-8 text-red-500">No underlying specified</div>
  }

  const isLoading = !accountsData || accountPortfolioQueries.isLoading
  const exposureTypes: ExposureType[] = ['position', 'nominal', 'delta', 'nc']

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/portfolio/${uploadId}${returnTab ? `?tab=${returnTab}` : ''}`)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Buy/Sell: {underlying}</h1>
          <p className="text-sm text-gray-500">Plan position adjustments to reach target exposure levels</p>
          {!isLoading && underlyingPrice === null && (
            <p className="text-sm text-yellow-600">Warning: Could not determine underlying price - stock calculations will not work</p>
          )}
          {!isLoading && underlyingPrice !== null && (
            <p className="text-sm text-gray-400">Underlying price: ${Number(underlyingPrice).toFixed(2)}</p>
          )}
        </div>
      </div>

      {/* As-Is Section */}
      <div className="card">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('as-is')}
        >
          <h2 className="text-lg font-semibold text-gray-900">Current Exposure (As-Is)</h2>
          {collapsedSections.has('as-is') ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
        {!collapsedSections.has('as-is') && (
          isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : accountExposures.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No exposure data found for {underlying}</p>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th style={{ width: '32px' }} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  <th style={{ width: '180px' }} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exposure Type</th>
                  {accountExposures.map(exp => {
                    const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                    return (
                      <th
                        key={exp.accountNumber}
                        style={{ width: '100px' }}
                        className={`px-2 py-3 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-100 transition-colors ${
                          isSelected ? 'text-gray-500' : 'text-gray-300'
                        }`}
                        onClick={() => toggleAccountSelection(exp.accountId)}
                        title={isSelected ? 'Click to exclude from calculations' : 'Click to include in calculations'}
                      >
                        <div className="flex flex-col items-end">
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleAccountSelection(exp.accountId)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-2.5 w-2.5 rounded border-gray-200 accent-gray-300 focus:ring-0 cursor-pointer opacity-60 hover:opacity-100"
                            />
                            <span>{exp.accountNumber}</span>
                          </div>
                          <span className="text-[10px] font-normal text-gray-400">
                            ${exp.portfolioValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      </th>
                    )
                  })}
                  <th style={{ width: '100px' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-blue-50">Wtd Avg</th>
                  <th style={{ width: '100px' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-yellow-50">Target</th>
                </tr>
              </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Quantity Row */}
              {(() => {
                const qtyKey = 'asis-quantity'
                const isQtyExpanded = expandedRows.has(qtyKey)
                return (
                  <React.Fragment key="quantity">
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRow(qtyKey)}
                    >
                      <td className="px-4 py-3">
                        {isQtyExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">Quantity</td>
                      {accountExposures.map(exp => {
                        const adjQty = calcAdjustedQty(exp.positions)
                        const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                        return (
                          <td key={exp.accountNumber} className={`px-4 py-3 text-right text-sm ${isSelected ? '' : 'text-gray-300'}`}>
                            {adjQty === 'complex' ? 'complex' : formatQuantity(adjQty)}
                          </td>
                        )
                      })}
                      <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                        {(() => {
                          const perAccountQtys = accountExposures.map(exp => calcAdjustedQty(exp.positions))
                          if (perAccountQtys.some(q => q === 'complex')) return 'complex'
                          const numericQtys = perAccountQtys.filter((q): q is number => typeof q === 'number')
                          return formatQuantity(numericQtys.reduce((sum, q) => sum + q, 0))
                        })()}
                      </td>
                      <td className="px-4 py-3 bg-yellow-50"></td>
                    </tr>
                    {isQtyExpanded && allSymbols.map((symbol, idx) => (
                      <tr key={`${qtyKey}-${idx}`} className="bg-gray-50">
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2 text-sm text-gray-600 pl-8">{symbol}</td>
                        {accountExposures.map(exp => {
                          const pos = exp.positions.find(p => p.symbol === symbol)
                          const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                          return (
                            <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                              {pos ? formatQuantity(pos.quantity) : '-'}
                            </td>
                          )
                        })}
                        <td className="px-4 py-2 bg-blue-50"></td>
                        <td className="px-4 py-2 bg-yellow-50"></td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })()}
              {exposureTypes.map(type => {
                const key = `asis-${type}`
                const isExpanded = expandedRows.has(key)
                const isActive = activeTarget === type

                return (
                  <React.Fragment key={type}>
                    <tr
                      className={`hover:bg-gray-50 cursor-pointer ${isActive ? 'bg-yellow-50/50' : ''}`}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).tagName !== 'INPUT') {
                          toggleRow(key)
                        }
                      }}
                    >
                      <td className="px-4 py-3">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{exposureLabels[type]}</td>
                      {accountExposures.map(exp => {
                        const val = type === 'position' ? exp.positionPct :
                                    type === 'nominal' ? exp.nominalPct :
                                    type === 'delta' ? exp.deltaPct : exp.ncPct
                        const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                        return (
                          <td key={exp.accountNumber} className={`px-4 py-3 text-right text-sm ${isSelected ? '' : 'text-gray-300'}`}>
                            {formatPercent(val)}
                          </td>
                        )
                      })}
                      <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                        {formatPercent(calculateWeightedAvg(exp =>
                          type === 'position' ? exp.positionPct :
                          type === 'nominal' ? exp.nominalPct :
                          type === 'delta' ? exp.deltaPct : exp.ncPct
                        ))}
                      </td>
                      <td className="px-4 py-3 text-right bg-yellow-50" onClick={e => e.stopPropagation()}>
                        <div className="inline-flex items-center">
                          <input
                            type="number"
                            step="0.1"
                            data-target-type={type}
                            value={targetValues[type]}
                            onChange={e => handleTargetChange(type, e.target.value)}
                            onKeyDown={e => handleKeyDown(e, type)}
                            placeholder="0.00"
                            className="w-16 px-2 py-1 text-right border border-gray-300 rounded-l text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <span className="px-2 py-1 bg-gray-100 border border-l-0 border-gray-300 rounded-r text-sm text-gray-500">%</span>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded rows showing individual securities */}
                    {isExpanded && allSymbols.map((symbol, idx) => (
                      <tr key={`${key}-${idx}`} className="bg-gray-50">
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2 text-sm text-gray-600 pl-8">{symbol}</td>
                        {accountExposures.map(exp => {
                          const accPos = exp.positions.find(p => p.symbol === symbol)
                          const val = type === 'position' ? accPos?.positionPct :
                                      type === 'nominal' ? accPos?.nominalPct :
                                      type === 'delta' ? accPos?.deltaPct : accPos?.ncPct
                          const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                          return (
                            <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                              {formatPercent(val)}
                            </td>
                          )
                        })}
                        <td className="px-4 py-2 bg-blue-50"></td>
                        <td className="px-4 py-2 bg-yellow-50"></td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
              {/* NC Spendable Cash % Row (Collapsible) */}
              {(() => {
                const isExpanded = ncSpendableExpanded.has('as-is')
                const isArtificialExpanded = ncArtificialExpanded.has('as-is')

                // Calculate NC Spendable Cash for each account
                // NC Spendable = Cash - Min Cash - NC Artificial Cash
                const ncSpendableByAccount: Record<number, {
                  cashPct: number | null
                  minCashPct: number
                  ncArtificialPct: number | null
                  ncSpendablePct: number | null
                }> = {}

                for (const exp of accountExposures) {
                  const account = includedAccounts.find(a => a.id === exp.accountId)
                  const minCashPct = (account?.min_cash_pct ?? 0) / 100
                  const ncArtificial = ncArtificialCashByAccount[exp.accountId]
                  const ncArtificialPct = ncArtificial?.totalPct ?? 0

                  const ncSpendablePct = exp.cashPct !== null
                    ? exp.cashPct - minCashPct - ncArtificialPct
                    : null

                  ncSpendableByAccount[exp.accountId] = {
                    cashPct: exp.cashPct,
                    minCashPct,
                    ncArtificialPct,
                    ncSpendablePct
                  }
                }

                // Calculate weighted averages
                const wtdAvgNcSpendable = (() => {
                  let sum = 0, totalWeight = 0
                  for (const exp of selectedAccountExposures) {
                    const data = ncSpendableByAccount[exp.accountId]
                    if (data?.ncSpendablePct !== null && exp.portfolioValue > 0) {
                      sum += data.ncSpendablePct * exp.portfolioValue
                      totalWeight += exp.portfolioValue
                    }
                  }
                  return totalWeight > 0 ? sum / totalWeight : null
                })()

                const wtdAvgMinCash = (() => {
                  let sum = 0, totalWeight = 0
                  for (const exp of selectedAccountExposures) {
                    const data = ncSpendableByAccount[exp.accountId]
                    if (exp.portfolioValue > 0) {
                      sum += data.minCashPct * exp.portfolioValue
                      totalWeight += exp.portfolioValue
                    }
                  }
                  return totalWeight > 0 ? sum / totalWeight : null
                })()

                const wtdAvgNcArtificial = (() => {
                  let sum = 0, totalWeight = 0
                  for (const exp of selectedAccountExposures) {
                    const data = ncSpendableByAccount[exp.accountId]
                    if (data?.ncArtificialPct !== null && exp.portfolioValue > 0) {
                      sum += data.ncArtificialPct * exp.portfolioValue
                      totalWeight += exp.portfolioValue
                    }
                  }
                  return totalWeight > 0 ? sum / totalWeight : null
                })()

                return (
                  <>
                    {/* Main NC Spendable Cash Row */}
                    <tr
                      className="hover:bg-gray-50 bg-green-50/50 cursor-pointer"
                      onClick={() => setNcSpendableExpanded(prev => {
                        const next = new Set(prev)
                        if (next.has('as-is')) next.delete('as-is')
                        else next.add('as-is')
                        return next
                      })}
                    >
                      <td className="px-4 py-3 text-sm">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">NC Spendable Cash %</td>
                      {accountExposures.map(exp => {
                        const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                        const data = ncSpendableByAccount[exp.accountId]
                        return (
                          <td key={exp.accountNumber} className={`px-4 py-3 text-right text-sm ${isSelected ? '' : 'text-gray-300'}`}>
                            {formatPercent(data?.ncSpendablePct)}
                          </td>
                        )
                      })}
                      <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                        {formatPercent(wtdAvgNcSpendable)}
                      </td>
                      <td className="px-4 py-3 bg-yellow-50"></td>
                    </tr>

                    {/* Expanded breakdown rows */}
                    {isExpanded && (
                      <>
                        {/* Cash % sub-row */}
                        <tr className="bg-green-50/30">
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2 text-sm text-gray-600 pl-8">Cash %</td>
                          {accountExposures.map(exp => {
                            const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                            return (
                              <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                {formatPercent(exp.cashPct)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                            {formatPercent(calculateWeightedAvg(exp => exp.cashPct))}
                          </td>
                          <td className="px-4 py-2 bg-yellow-50/50"></td>
                        </tr>

                        {/* Min Cash % sub-row */}
                        <tr className="bg-green-50/30">
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2 text-sm text-gray-600 pl-8">Min Cash %</td>
                          {accountExposures.map(exp => {
                            const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                            const data = ncSpendableByAccount[exp.accountId]
                            return (
                              <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                {formatPercent(data?.minCashPct != null ? -data.minCashPct : null)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                            {formatPercent(wtdAvgMinCash != null ? -wtdAvgMinCash : null)}
                          </td>
                          <td className="px-4 py-2 bg-yellow-50/50"></td>
                        </tr>

                        {/* NC Artificial Cash % sub-row (collapsible to show per-underlying) */}
                        <tr
                          className="bg-green-50/30 cursor-pointer hover:bg-green-50/50"
                          onClick={(e) => {
                            e.stopPropagation()
                            setNcArtificialExpanded(prev => {
                              const next = new Set(prev)
                              if (next.has('as-is')) next.delete('as-is')
                              else next.add('as-is')
                              return next
                            })
                          }}
                        >
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2 text-sm text-gray-600 pl-8">
                            <span title="Cash reserved for inverse covered calls (short stock + long call). Note: May be approximate with foreign currencies, multiple options per underlying, or >100 shares short.">
                              NC Artificial Cash %
                            </span>
                          </td>
                          {accountExposures.map(exp => {
                            const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                            const data = ncSpendableByAccount[exp.accountId]
                            return (
                              <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                {formatPercent(data?.ncArtificialPct != null ? -data.ncArtificialPct : null)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                            {formatPercent(wtdAvgNcArtificial != null ? -wtdAvgNcArtificial : null)}
                          </td>
                          <td className="px-4 py-2 bg-yellow-50/50"></td>
                        </tr>

                        {/* NC Artificial Cash per-underlying breakdown */}
                        {isArtificialExpanded && (() => {
                          // Collect all unique underlyings across all accounts
                          const allUnderlyings = new Set<string>()
                          for (const exp of accountExposures) {
                            const data = ncArtificialCashByAccount[exp.accountId]
                            data?.items.forEach(item => allUnderlyings.add(item.underlying))
                          }

                          return Array.from(allUnderlyings).sort().map(underlyingSymbol => (
                            <tr key={`artificial-${underlyingSymbol}`} className="bg-green-50/20">
                              <td className="px-4 py-1"></td>
                              <td className="px-4 py-1 text-xs text-gray-500 pl-16">{underlyingSymbol}</td>
                              {accountExposures.map(exp => {
                                const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                const data = ncArtificialCashByAccount[exp.accountId]
                                const item = data?.items.find(i => i.underlying === underlyingSymbol)
                                const pct = item && exp.portfolioValue > 0
                                  ? item.artificialCash / exp.portfolioValue
                                  : null
                                return (
                                  <td key={exp.accountNumber} className={`px-4 py-1 text-right text-xs ${isSelected ? 'text-gray-400' : 'text-gray-300'}`}>
                                    {pct !== null ? formatPercent(-pct) : '-'}
                                  </td>
                                )
                              })}
                              <td className="px-4 py-1 bg-blue-50/30"></td>
                              <td className="px-4 py-1 bg-yellow-50/30"></td>
                            </tr>
                          ))
                        })()}
                      </>
                    )}
                  </>
                )
              })()}
            </tbody>
          </table>
        </div>
        ))}
      </div>

      {/* Securities Selector */}
      <div className="card">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('securities')}
        >
          <h2 className="text-lg font-semibold text-gray-900">Securities to Use</h2>
          {collapsedSections.has('securities') ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
        {!collapsedSections.has('securities') && (
        <>
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-500">
            Select the securities to use when calculating buy/sell orders.
          </p>
          {Object.keys(customPrices).length > 0 && (
            <button
              onClick={() => setCustomPrices({})}
              className="text-xs px-2 py-1 text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-gray-50"
            >
              Reset Prices
            </button>
          )}
        </div>
        <div className="space-y-2">
          {orderedSecuritiesForDisplay.map((security) => {
            const isSelected = selectedSecurities.includes(security.id)
            // Calculate order number among selected securities
            const selectedOrder = isSelected
              ? orderedSecuritiesForDisplay
                  .filter(s => selectedSecurities.includes(s.id))
                  .findIndex(s => s.id === security.id) + 1
              : null
            return (
              <div
                key={security.id}
                draggable
                onDragStart={(e) => handleDragStart(e, security.id)}
                onDragOver={(e) => handleDragOver(e, security.id)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-grab active:cursor-grabbing ${
                  isSelected
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-gray-200 bg-gray-50'
                } ${draggedId === security.id ? 'opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSecurity(security.id)}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                <div className="flex items-center gap-1 text-gray-400">
                  <GripVertical className="h-4 w-4" />
                  {selectedOrder !== null && (
                    <span className="text-xs font-bold text-primary-600 w-4">{selectedOrder}</span>
                  )}
                </div>
                <div className="flex-1">
                  <span className="font-medium">{security.displayName}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                    security.type === 'stock' ? 'bg-blue-100 text-blue-700' :
                    security.type === 'call' ? 'bg-green-100 text-green-700' :
                    security.type === 'put' ? 'bg-red-100 text-red-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {security.type === 'covered_call' ? 'Covered Call' : security.type}
                  </span>
                </div>
                {/* Price Input with Currency */}
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-500">{security.currency}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={customPrices[security.id] ?? (typeof security.price === 'number' ? String(security.price) : '')}
                    onChange={(e) => {
                      setCustomPrices(prev => ({ ...prev, [security.id]: e.target.value }))
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={`w-20 px-2 py-1 text-right border rounded text-sm ${
                      customPrices[security.id] !== undefined
                        ? 'border-yellow-400 bg-yellow-50'
                        : 'border-gray-300'
                    }`}
                    placeholder={typeof security.price === 'number' ? security.price.toFixed(2) : '-'}
                  />
                  {/* Show USD equivalent for non-USD currencies */}
                  {security.currency !== 'USD' && (() => {
                    const priceText = customPrices[security.id]
                    const price = priceText !== undefined ? parseFloat(priceText) : security.price
                    if (price === null || price === undefined || isNaN(price)) return null
                    const fxRate = fxRates[security.currency] ?? 1
                    const usdPrice = price * fxRate
                    return (
                      <span className="text-gray-400 ml-1">
                        USD {usdPrice.toFixed(2)}
                      </span>
                    )
                  })()}
                </div>
                {/* Allow Short Toggle */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>Short</span>
                  <button
                    onClick={() => setAllowShort(prev => ({ ...prev, [security.id]: !prev[security.id] }))}
                    className={`relative w-8 h-4 rounded-full transition-colors ${
                      allowShort[security.id] ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                      allowShort[security.id] ? 'translate-x-4' : ''
                    }`} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
        </>
        )}
      </div>

      {/* Calculate Orders Button */}
      <div className="flex justify-center">
        <button
          onClick={() => handleCalculate()}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-lg shadow-md"
        >
          Calculate Orders
        </button>
      </div>

      {/* Calculation Output Section */}
      <div className="card">
        <div
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('output')}
        >
          <h2 className="text-lg font-semibold text-gray-900">Calculation Output</h2>
          {collapsedSections.has('output') ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
        {!collapsedSections.has('output') && (
          calculatedOrders.length === 0 ? (
            <div className="text-center py-8">
              {calcStatus ? (
                <p className="text-yellow-600">{calcStatus}</p>
              ) : (
                <p className="text-gray-500">Enter a target exposure in the table above and click Calculate Orders to see recommendations.</p>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Inputs Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Calculation Inputs</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Target: </span>
                    <span className="font-medium">
                      {targetValues[activeTarget!]}% {exposureLabels[activeTarget!]}
                    </span>
                  </div>
                  <div className="border-l pl-4">
                    <span className="text-gray-500">Securities: </span>
                    {(() => {
                      const orderedSelected = securitiesOrder
                        .filter(id => selectedSecurities.includes(id))
                        .map(id => availableSecurities.find(s => s.id === id)!)
                        .filter(Boolean)
                      return orderedSelected.map((sec, idx) => {
                        const price = appliedCustomPrices[sec.id] ?? sec.price
                        const isNonUsd = sec.currency !== 'USD'
                        const fxRate = fxRates[sec.currency] ?? 1
                        return (
                          <span key={sec.id} className="font-medium">
                            {idx > 0 && ', '}
                            {sec.displayName}
                            {price !== null && (
                              <span className="text-gray-400 ml-1">
                                @{isNonUsd ? `${sec.currency} ` : '$'}{typeof price === 'number' ? price.toFixed(2) : '-'}
                                {isNonUsd && (
                                  <span className="text-xs">
                                    {' '}USD {(price * fxRate).toFixed(2)}
                                  </span>
                                )}
                              </span>
                            )}
                          </span>
                        )
                      })
                    })()}
                  </div>
                </div>
                {calcStatus && (
                  <p className="mt-2 text-sm text-yellow-600">{calcStatus}</p>
                )}
              </div>

              {/* Recommendation Table */}
              {(() => {
                const orderSecurities = [...new Set(calculatedOrders.map(o => o.security))]

                // Build per-security data
                const ordersBySecurityData = orderSecurities.map(security => {
                  const securityOrders = calculatedOrders.filter(o => o.security === security)
                  const secInfo = availableSecurities.find(s => s.symbol === security || s.displayName === security)

                  const qtyByAccount: Record<string, number> = {}
                  const usdByAccount: Record<string, number> = {}
                  const posPctByAccount: Record<string, number> = {}
                  const nomPctByAccount: Record<string, number> = {}
                  const deltaPctByAccount: Record<string, number> = {}
                  const ncPctByAccount: Record<string, number> = {}

                  // Helper to get custom price in USD
                  const getCustomPriceInUsdLocal = (secId: string, sec: SecurityItem): number | null => {
                    const customPrice = appliedCustomPrices[secId]
                    if (customPrice === null || customPrice === undefined) return null
                    const inputCurrency = appliedCustomPriceCurrencies[secId] ?? sec.currency
                    return customPrice * (fxRates[inputCurrency] ?? 1)
                  }

                  const calculateUsdValue = (signedQty: number, positions: AccountExposure['positions']): number => {
                    if (secInfo?.type === 'covered_call') {
                      const ccSecId = `covered-${secInfo.callSymbol}`
                      const ccCustomPriceUsd = getCustomPriceInUsdLocal(ccSecId, secInfo)
                      if (ccCustomPriceUsd !== null) {
                        return signedQty * 100 * ccCustomPriceUsd
                      }
                      const stockSecId = `stock-${underlying}`
                      const callSecId = `option-${secInfo.callSymbol}`
                      const stockSec = availableSecurities.find(s => s.id === stockSecId)
                      const callSec = availableSecurities.find(s => s.id === callSecId)
                      const customStockPriceUsd = stockSec ? getCustomPriceInUsdLocal(stockSecId, stockSec) : null
                      const customCallPriceUsd = callSec ? getCustomPriceInUsdLocal(callSecId, callSec) : null
                      const stockPos = positions.find(p => p.symbol === underlying)
                      const secFxRate = fxRates[secInfo.currency] ?? 1
                      const stockPrice = customStockPriceUsd ?? (stockPos && stockPos.quantity !== 0
                        ? (stockPos.value / Math.abs(stockPos.quantity)) * secFxRate  // value is native currency
                        : (underlyingPrice || 0) * secFxRate)
                      const callPos = positions.find(p => p.symbol === secInfo.callSymbol)
                      const callPrice = customCallPriceUsd ?? (callPos && callPos.quantity !== 0
                        ? (Math.abs(callPos.value / callPos.quantity) / 100) * secFxRate  // value is native currency
                        : (secInfo.price || 0) * secFxRate)
                      return signedQty * 100 * (stockPrice - callPrice)
                    } else if (secInfo?.type === 'call' || secInfo?.type === 'put') {
                      const customPriceUsd = getCustomPriceInUsdLocal(secInfo.id, secInfo)
                      if (customPriceUsd !== null) {
                        return signedQty * customPriceUsd * 100
                      }
                      const optPos = positions.find(p => p.symbol === secInfo.symbol)
                      const secFxRate = fxRates[secInfo.currency] ?? 1
                      const pricePerContract = optPos && optPos.quantity !== 0
                        ? Math.abs(optPos.value / optPos.quantity) * secFxRate  // value is native currency
                        : (secInfo.price || 0) * secFxRate * 100
                      return signedQty * pricePerContract
                    } else {
                      const customPriceUsd = secInfo ? getCustomPriceInUsdLocal(secInfo.id, secInfo) : null
                      if (customPriceUsd !== null) {
                        return signedQty * customPriceUsd
                      }
                      const stockPos = positions.find(p => p.symbol === secInfo?.symbol)
                      const secFxRate = secInfo ? (fxRates[secInfo.currency] ?? 1) : 1
                      const pricePerShare = stockPos && stockPos.quantity !== 0
                        ? (stockPos.value / Math.abs(stockPos.quantity)) * secFxRate  // value is native currency
                        : (secInfo?.price || underlyingPrice || 0) * secFxRate
                      return signedQty * pricePerShare
                    }
                  }

                  for (const order of securityOrders) {
                    const signedQty = order.action === 'Buy' ? order.quantity : -order.quantity
                    const exp = accountExposures.find(e => e.accountNumber === order.accountNumber)
                    const usdValue = calculateUsdValue(signedQty, exp?.positions || [])

                    qtyByAccount[order.accountNumber] = signedQty
                    usdByAccount[order.accountNumber] = usdValue

                    if (exp && secInfo) {
                      const contrib = secInfo.perUnitContributions[exp.accountId]
                      if (contrib) {
                        posPctByAccount[order.accountNumber] = signedQty * contrib.position
                        nomPctByAccount[order.accountNumber] = signedQty * contrib.nominal
                        deltaPctByAccount[order.accountNumber] = signedQty * contrib.delta
                        ncPctByAccount[order.accountNumber] = signedQty * contrib.nc
                      }
                    }
                  }

                  return {
                    security,
                    qtyByAccount,
                    usdByAccount,
                    posPctByAccount,
                    nomPctByAccount,
                    deltaPctByAccount,
                    ncPctByAccount,
                  }
                })

                // Aggregate totals per account
                const totals = {
                  qtyByAccount: {} as Record<string, number>,
                  usdByAccount: {} as Record<string, number>,
                  posPctByAccount: {} as Record<string, number>,
                  nomPctByAccount: {} as Record<string, number>,
                  deltaPctByAccount: {} as Record<string, number>,
                  ncPctByAccount: {} as Record<string, number>,
                }

                for (const row of ordersBySecurityData) {
                  for (const acc of Object.keys(row.qtyByAccount)) {
                    totals.qtyByAccount[acc] = (totals.qtyByAccount[acc] || 0) + row.qtyByAccount[acc]
                    totals.usdByAccount[acc] = (totals.usdByAccount[acc] || 0) + row.usdByAccount[acc]
                    totals.posPctByAccount[acc] = (totals.posPctByAccount[acc] || 0) + (row.posPctByAccount[acc] || 0)
                    totals.nomPctByAccount[acc] = (totals.nomPctByAccount[acc] || 0) + (row.nomPctByAccount[acc] || 0)
                    totals.deltaPctByAccount[acc] = (totals.deltaPctByAccount[acc] || 0) + (row.deltaPctByAccount[acc] || 0)
                    totals.ncPctByAccount[acc] = (totals.ncPctByAccount[acc] || 0) + (row.ncPctByAccount[acc] || 0)
                  }
                }

                // Calculate grand totals and weighted averages
                const grandTotalQty = Object.values(totals.qtyByAccount).reduce((sum, v) => sum + v, 0)
                const grandTotalUsd = Object.values(totals.usdByAccount).reduce((sum, v) => sum + v, 0)

                const calcWeightedAvg = (byAccount: Record<string, number>) => {
                  let weighted = 0, totalWeight = 0
                  for (const exp of selectedAccountExposures) {
                    const val = byAccount[exp.accountNumber]
                    if (val !== undefined && exp.portfolioValue > 0) {
                      weighted += val * exp.portfolioValue
                      totalWeight += exp.portfolioValue
                    }
                  }
                  return totalWeight > 0 ? weighted / totalWeight : null
                }

                const orderMetrics: Array<{
                  key: string
                  label: string
                  getAccountValue: (acc: string) => number | undefined
                  getSecurityValue: (row: typeof ordersBySecurityData[0], acc: string) => number | undefined
                  getSummary: () => number | null
                  formatValue: (val: number) => string
                  summaryLabel: string
                }> = [
                  {
                    key: 'qty',
                    label: 'Quantity',
                    getAccountValue: acc => totals.qtyByAccount[acc],
                    getSecurityValue: (row, acc) => row.qtyByAccount[acc],
                    getSummary: () => grandTotalQty,
                    formatValue: val => (val > 0 ? '+' : '') + formatQuantity(val),
                    summaryLabel: 'Total',
                  },
                  {
                    key: 'usd',
                    label: 'USD Value',
                    getAccountValue: acc => totals.usdByAccount[acc],
                    getSecurityValue: (row, acc) => row.usdByAccount[acc],
                    getSummary: () => grandTotalUsd,
                    formatValue: val => (val >= 0 ? '' : '-') + '$' + Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
                    summaryLabel: 'Total',
                  },
                  {
                    key: 'pos',
                    label: 'Position %',
                    getAccountValue: acc => totals.posPctByAccount[acc],
                    getSecurityValue: (row, acc) => row.posPctByAccount[acc],
                    getSummary: () => calcWeightedAvg(totals.posPctByAccount),
                    formatValue: val => formatPercent(val),
                    summaryLabel: 'Wtd Avg',
                  },
                  {
                    key: 'nom',
                    label: 'Nominal %',
                    getAccountValue: acc => totals.nomPctByAccount[acc],
                    getSecurityValue: (row, acc) => row.nomPctByAccount[acc],
                    getSummary: () => calcWeightedAvg(totals.nomPctByAccount),
                    formatValue: val => formatPercent(val),
                    summaryLabel: 'Wtd Avg',
                  },
                  {
                    key: 'delta',
                    label: 'Delta %',
                    getAccountValue: acc => totals.deltaPctByAccount[acc],
                    getSecurityValue: (row, acc) => row.deltaPctByAccount[acc],
                    getSummary: () => calcWeightedAvg(totals.deltaPctByAccount),
                    formatValue: val => formatPercent(val),
                    summaryLabel: 'Wtd Avg',
                  },
                  {
                    key: 'nc',
                    label: 'NC %',
                    getAccountValue: acc => totals.ncPctByAccount[acc],
                    getSecurityValue: (row, acc) => row.ncPctByAccount[acc],
                    getSummary: () => calcWeightedAvg(totals.ncPctByAccount),
                    formatValue: val => formatPercent(val),
                    summaryLabel: 'Wtd Avg',
                  },
                ]

                return (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Recommended Orders</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 table-fixed">
                        <thead className="bg-gray-50">
                          <tr>
                            <th style={{ width: '32px' }} className="px-2 py-3"></th>
                            <th style={{ width: '180px' }} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                            {accountExposures.map(exp => (
                              <th key={exp.accountNumber} style={{ width: '100px' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                {exp.accountNumber}
                              </th>
                            ))}
                            <th style={{ width: '100px' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-blue-50">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {orderMetrics.map(metric => {
                            const rowKey = `orders-${metric.key}`
                            const isExpanded = expandedRows.has(rowKey)
                            return (
                              <React.Fragment key={metric.key}>
                                <tr
                                  className="hover:bg-gray-50 cursor-pointer"
                                  onClick={() => toggleRow(rowKey)}
                                >
                                  <td className="px-4 py-3">
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-900">{metric.label}</td>
                                  {accountExposures.map(exp => {
                                    const val = metric.getAccountValue(exp.accountNumber)
                                    return (
                                      <td key={exp.accountNumber} className="px-4 py-3 text-right text-sm font-medium">
                                        {val !== undefined ? metric.formatValue(val) : <span className="text-gray-300">-</span>}
                                      </td>
                                    )
                                  })}
                                  <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                                    {metric.getSummary() !== null ? metric.formatValue(metric.getSummary()!) : '-'}
                                  </td>
                                </tr>
                                {isExpanded && ordersBySecurityData.map(row => (
                                  <tr key={`${metric.key}-${row.security}`} className="bg-gray-50">
                                    <td className="px-4 py-2"></td>
                                    <td className="px-4 py-2 text-sm text-gray-600 pl-8">{row.security}</td>
                                    {accountExposures.map(exp => {
                                      const val = metric.getSecurityValue(row, exp.accountNumber)
                                      return (
                                        <td key={exp.accountNumber} className="px-4 py-2 text-right text-sm text-gray-500">
                                          {val !== undefined ? metric.formatValue(val) : <span className="text-gray-300">-</span>}
                                        </td>
                                      )
                                    })}
                                    <td className="px-4 py-2 bg-blue-50"></td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              })()}

              {/* Would-be Values Table */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Would-be Values (After Orders)</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th style={{ width: '32px' }} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                        <th style={{ width: '180px' }} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                        {accountExposures.map(exp => (
                          <th key={exp.accountNumber} style={{ width: '100px' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            {exp.accountNumber}
                          </th>
                        ))}
                        <th style={{ width: '100px' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-blue-50">Wtd Avg</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {/* Quantity Row */}
                      {(() => {
                        const qtyKey = 'wouldbe-quantity'
                        const isQtyExpanded = expandedRows.has(qtyKey)
                        return (
                          <React.Fragment key="wouldbe-quantity">
                            <tr
                              className="hover:bg-gray-50 cursor-pointer bg-yellow-50"
                              onClick={() => toggleRow(qtyKey)}
                            >
                              <td className="px-4 py-3">
                                {isQtyExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900">Quantity</td>
                              {accountExposures.map(exp => {
                                const currentQty = calcAdjustedQty(exp.positions)
                                let orderDelta = 0
                                for (const order of calculatedOrders.filter(o => o.accountNumber === exp.accountNumber)) {
                                  const sec = availableSecurities.find(s => s.symbol === order.security || s.displayName === order.security)
                                  const multiplier = (sec?.type === 'call' || sec?.type === 'put') ? 100 : 1
                                  if (sec?.type === 'covered_call') {
                                    orderDelta += (order.action === 'Buy' ? 100 : -100) * order.quantity
                                  } else {
                                    orderDelta += (order.action === 'Buy' ? multiplier : -multiplier) * order.quantity
                                  }
                                }
                                const isComplex = currentQty === 'complex'
                                const projectedQty = isComplex ? 'complex' : (currentQty as number) + orderDelta
                                const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                return (
                                  <td key={exp.accountNumber} className={`px-4 py-3 text-right text-sm ${
                                    !isSelected ? 'text-gray-300' : 'font-medium text-primary-700'
                                  }`}>
                                    {projectedQty === 'complex' ? 'complex' : formatQuantity(projectedQty)}
                                  </td>
                                )
                              })}
                              <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                                {(() => {
                                  const perAccountQtys = accountExposures.map(exp => {
                                    const currentQty = calcAdjustedQty(exp.positions)
                                    if (currentQty === 'complex') return 'complex' as const
                                    let orderDelta = 0
                                    for (const order of calculatedOrders.filter(o => o.accountNumber === exp.accountNumber)) {
                                      const sec = availableSecurities.find(s => s.symbol === order.security || s.displayName === order.security)
                                      const multiplier = (sec?.type === 'call' || sec?.type === 'put') ? 100 : 1
                                      if (sec?.type === 'covered_call') {
                                        orderDelta += (order.action === 'Buy' ? 100 : -100) * order.quantity
                                      } else {
                                        orderDelta += (order.action === 'Buy' ? multiplier : -multiplier) * order.quantity
                                      }
                                    }
                                    return currentQty + orderDelta
                                  })
                                  if (perAccountQtys.some(q => q === 'complex')) return 'complex'
                                  const numericQtys = perAccountQtys.filter((q): q is number => typeof q === 'number')
                                  return formatQuantity(numericQtys.reduce((sum, q) => sum + q, 0))
                                })()}
                              </td>
                            </tr>
                            {isQtyExpanded && allSymbols.map((symbol, idx) => (
                              <tr key={`${qtyKey}-${idx}`} className="bg-yellow-50/50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 text-sm text-gray-600 pl-8">{symbol}</td>
                                {accountExposures.map(exp => {
                                  const pos = exp.positions.find(p => p.symbol === symbol)
                                  const currentQty = pos?.quantity || 0
                                  let orderDelta = 0
                                  for (const order of calculatedOrders.filter(o => o.accountNumber === exp.accountNumber)) {
                                    const sec = availableSecurities.find(s =>
                                      s.symbol === order.security || s.displayName === order.security
                                    )
                                    if (!sec) continue
                                    if (sec.type === 'covered_call') {
                                      if (symbol === underlying) {
                                        orderDelta += (order.action === 'Buy' ? 100 : -100) * order.quantity
                                      } else if (symbol === sec.callSymbol) {
                                        orderDelta += (order.action === 'Buy' ? -1 : 1) * order.quantity
                                      }
                                    } else if (sec.symbol === symbol) {
                                      orderDelta += (order.action === 'Buy' ? 1 : -1) * order.quantity
                                    }
                                  }
                                  const projectedQty = currentQty + orderDelta
                                  const hasChange = orderDelta !== 0
                                  const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                  return (
                                    <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${
                                      !isSelected ? 'text-gray-300' : hasChange ? 'font-medium text-primary-700' : 'text-gray-500'
                                    }`}>
                                      {formatQuantity(projectedQty)}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 bg-blue-50"></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        )
                      })()}

                      {/* Exposure type rows */}
                      {exposureTypes.map(type => {
                        const key = `wouldbe-${type}`
                        const isExpanded = expandedRows.has(key)
                        const isActive = activeTarget === type

                        return (
                          <React.Fragment key={type}>
                            <tr
                              className={`hover:bg-gray-50 cursor-pointer ${isActive ? 'bg-yellow-50' : ''}`}
                              onClick={() => toggleRow(key)}
                            >
                              <td className="px-4 py-3">
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900">{exposureLabels[type]}</td>
                              {accountExposures.map(exp => {
                                const projected = projectedExposures?.[exp.accountNumber]
                                const val = projected
                                  ? (type === 'position' ? projected.positionPct :
                                     type === 'nominal' ? projected.nominalPct :
                                     type === 'delta' ? projected.deltaPct : projected.ncPct)
                                  : (type === 'position' ? exp.positionPct :
                                     type === 'nominal' ? exp.nominalPct :
                                     type === 'delta' ? exp.deltaPct : exp.ncPct)
                                const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                return (
                                  <td key={exp.accountNumber} className={`px-4 py-3 text-right text-sm ${
                                    !isSelected ? 'text-gray-300' : projected && isActive ? 'font-medium text-primary-700' : ''
                                  }`}>
                                    {formatPercent(val)}
                                  </td>
                                )
                              })}
                              <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                                {formatPercent(calculateWeightedAvg(exp => {
                                  const projected = projectedExposures?.[exp.accountNumber]
                                  return projected
                                    ? (type === 'position' ? projected.positionPct :
                                       type === 'nominal' ? projected.nominalPct :
                                       type === 'delta' ? projected.deltaPct : projected.ncPct)
                                    : (type === 'position' ? exp.positionPct :
                                       type === 'nominal' ? exp.nominalPct :
                                       type === 'delta' ? exp.deltaPct : exp.ncPct)
                                }))}
                              </td>
                            </tr>
                            {isExpanded && allSymbols.map((symbol, idx) => (
                              <tr key={`${key}-${idx}`} className="bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 text-sm text-gray-600 pl-8">{symbol}</td>
                                {accountExposures.map(exp => {
                                  const accPos = exp.positions.find(p => p.symbol === symbol)
                                  const currentVal = type === 'position' ? accPos?.positionPct :
                                              type === 'nominal' ? accPos?.nominalPct :
                                              type === 'delta' ? accPos?.deltaPct : accPos?.ncPct

                                  let orderDelta = 0
                                  for (const order of calculatedOrders.filter(o => o.accountNumber === exp.accountNumber)) {
                                    const sec = availableSecurities.find(s =>
                                      s.symbol === order.security || s.displayName === order.security
                                    )
                                    if (!sec) continue

                                    const contrib = sec.perUnitContributions[exp.accountId]
                                    if (!contrib) continue

                                    if (sec.type === 'covered_call') {
                                      if (symbol === underlying) {
                                        const stockSec = availableSecurities.find(s => s.type === 'stock')
                                        const stockContrib = stockSec?.perUnitContributions[exp.accountId]
                                        if (stockContrib) {
                                          const perUnit = type === 'position' ? stockContrib.position :
                                                          type === 'nominal' ? stockContrib.nominal :
                                                          type === 'delta' ? stockContrib.delta : stockContrib.nc
                                          const multiplier = order.action === 'Buy' ? 100 : -100
                                          orderDelta += multiplier * order.quantity * perUnit
                                        }
                                      } else if (symbol === sec.callSymbol) {
                                        const callSec = availableSecurities.find(s => s.symbol === sec.callSymbol)
                                        const callContrib = callSec?.perUnitContributions[exp.accountId]
                                        if (callContrib) {
                                          const perUnit = type === 'position' ? callContrib.position :
                                                          type === 'nominal' ? callContrib.nominal :
                                                          type === 'delta' ? callContrib.delta : callContrib.nc
                                          const multiplier = order.action === 'Buy' ? -1 : 1
                                          orderDelta += multiplier * order.quantity * perUnit
                                        }
                                      }
                                    } else if (sec.symbol === symbol) {
                                      const perUnit = type === 'position' ? contrib.position :
                                                      type === 'nominal' ? contrib.nominal :
                                                      type === 'delta' ? contrib.delta : contrib.nc
                                      const multiplier = order.action === 'Buy' ? 1 : -1
                                      orderDelta += multiplier * order.quantity * perUnit
                                    }
                                  }

                                  const projectedVal = (currentVal || 0) + orderDelta
                                  const hasChange = Math.abs(orderDelta) > 0.0000001
                                  const isSelected = selectedAccountIds?.has(exp.accountId) ?? true

                                  return (
                                    <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${
                                      !isSelected ? 'text-gray-300' : hasChange && activeTarget === type ? 'font-medium text-primary-700' : 'text-gray-500'
                                    }`}>
                                      {formatPercent(projectedVal)}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 bg-blue-50"></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        )
                      })}

                      {/* NC Spendable Cash % Row */}
                      {(() => {
                        const isExpanded = ncSpendableExpanded.has('wouldbe')
                        const isArtificialExpanded = ncArtificialExpanded.has('wouldbe')

                        // Helper to convert custom price to USD
                        const getCustomPriceUsd = (secId: string, sec: SecurityItem): number | null => {
                          const customPrice = appliedCustomPrices[secId]
                          if (customPrice === null || customPrice === undefined) return null
                          const inputCurrency = appliedCustomPriceCurrencies[secId] ?? sec.currency
                          return customPrice * (fxRates[inputCurrency] ?? 1)
                        }

                        const orderCashByAccount: Record<string, { cashDelta: number; orderCashPct: number | null }> = {}
                        for (const exp of accountExposures) {
                          let cashDelta = 0
                          for (const order of calculatedOrders.filter(o => o.accountNumber === exp.accountNumber)) {
                            const sec = availableSecurities.find(s =>
                              s.symbol === order.security || s.displayName === order.security
                            )
                            if (!sec) continue

                            const signedQty = order.action === 'Buy' ? order.quantity : -order.quantity
                            let usdValue = 0

                            if (sec.type === 'covered_call') {
                              const ccSecId = `covered-${sec.callSymbol}`
                              const ccCustomPriceUsd = getCustomPriceUsd(ccSecId, sec)
                              if (ccCustomPriceUsd !== null) {
                                usdValue = signedQty * 100 * ccCustomPriceUsd
                              } else {
                                const stockSecId = `stock-${underlying}`
                                const stockSec = availableSecurities.find(s => s.id === stockSecId)
                                const stockCustomPriceUsd = stockSec ? getCustomPriceUsd(stockSecId, stockSec) : null
                                const stockPos = exp.positions.find(p => p.symbol === underlying)
                                const secFxRate = fxRates[sec.currency] ?? 1
                                const stockPrice = stockCustomPriceUsd ?? (stockPos && stockPos.quantity !== 0
                                  ? (stockPos.value / Math.abs(stockPos.quantity)) * secFxRate  // value is native currency
                                  : (underlyingPrice || 0) * secFxRate)

                                const callSecId = `option-${sec.callSymbol}`
                                const callSec = availableSecurities.find(s => s.id === callSecId)
                                const callCustomPriceUsd = callSec ? getCustomPriceUsd(callSecId, callSec) : null
                                const callPos = exp.positions.find(p => p.symbol === sec.callSymbol)
                                const callPrice = callCustomPriceUsd ?? (callPos && callPos.quantity !== 0
                                  ? (Math.abs(callPos.value / callPos.quantity) / 100) * secFxRate  // value is native currency
                                  : (sec.price || 0) * secFxRate)

                                usdValue = signedQty * 100 * (stockPrice - callPrice)
                              }
                            } else if (sec.type === 'call' || sec.type === 'put') {
                              const customPriceUsd = getCustomPriceUsd(sec.id, sec)
                              if (customPriceUsd !== null) {
                                usdValue = signedQty * customPriceUsd * 100
                              } else {
                                const optPos = exp.positions.find(p => p.symbol === sec.symbol)
                                const secFxRate = fxRates[sec.currency] ?? 1
                                const pricePerContract = optPos && optPos.quantity !== 0
                                  ? Math.abs(optPos.value / optPos.quantity) * secFxRate  // value is native currency
                                  : (sec.price || 0) * secFxRate * 100
                                usdValue = signedQty * pricePerContract
                              }
                            } else {
                              const customPriceUsd = getCustomPriceUsd(sec.id, sec)
                              if (customPriceUsd !== null) {
                                usdValue = signedQty * customPriceUsd
                              } else {
                                const stockPos = exp.positions.find(p => p.symbol === sec.symbol)
                                const secFxRate = fxRates[sec.currency] ?? 1
                                const pricePerShare = stockPos && stockPos.quantity !== 0
                                  ? (stockPos.value / Math.abs(stockPos.quantity)) * secFxRate  // value is native currency
                                  : (sec.price || underlyingPrice || 0) * secFxRate
                                usdValue = signedQty * pricePerShare
                              }
                            }
                            cashDelta -= usdValue
                          }

                          orderCashByAccount[exp.accountNumber] = {
                            cashDelta,
                            orderCashPct: exp.portfolioValue > 0 ? -cashDelta / exp.portfolioValue : null
                          }
                        }

                        const ncSpendableByAccount: Record<number, {
                          cashPct: number | null
                          minCashPct: number
                          ncArtificialPct: number | null
                          orderCashPct: number | null
                          ncSpendablePct: number | null
                        }> = {}

                        for (const exp of accountExposures) {
                          const account = includedAccounts.find(a => a.id === exp.accountId)
                          const minCashPct = (account?.min_cash_pct ?? 0) / 100
                          const ncArtificial = ncArtificialCashByAccount[exp.accountId]
                          const ncArtificialPct = ncArtificial?.totalPct ?? 0
                          const orderData = orderCashByAccount[exp.accountNumber]
                          const orderCashPct = orderData?.orderCashPct ?? 0

                          const ncSpendablePct = exp.cashPct !== null
                            ? exp.cashPct - minCashPct - ncArtificialPct - orderCashPct
                            : null

                          ncSpendableByAccount[exp.accountId] = {
                            cashPct: exp.cashPct,
                            minCashPct,
                            ncArtificialPct,
                            orderCashPct,
                            ncSpendablePct
                          }
                        }

                        const wtdAvgNcSpendable = (() => {
                          let sum = 0, totalWeight = 0
                          for (const exp of selectedAccountExposures) {
                            const data = ncSpendableByAccount[exp.accountId]
                            if (data?.ncSpendablePct !== null && exp.portfolioValue > 0) {
                              sum += data.ncSpendablePct * exp.portfolioValue
                              totalWeight += exp.portfolioValue
                            }
                          }
                          return totalWeight > 0 ? sum / totalWeight : null
                        })()

                        const wtdAvgMinCash = (() => {
                          let sum = 0, totalWeight = 0
                          for (const exp of selectedAccountExposures) {
                            const data = ncSpendableByAccount[exp.accountId]
                            if (exp.portfolioValue > 0) {
                              sum += data.minCashPct * exp.portfolioValue
                              totalWeight += exp.portfolioValue
                            }
                          }
                          return totalWeight > 0 ? sum / totalWeight : null
                        })()

                        const wtdAvgNcArtificial = (() => {
                          let sum = 0, totalWeight = 0
                          for (const exp of selectedAccountExposures) {
                            const data = ncSpendableByAccount[exp.accountId]
                            if (data?.ncArtificialPct !== null && exp.portfolioValue > 0) {
                              sum += data.ncArtificialPct * exp.portfolioValue
                              totalWeight += exp.portfolioValue
                            }
                          }
                          return totalWeight > 0 ? sum / totalWeight : null
                        })()

                        const wtdAvgOrderCash = (() => {
                          let sum = 0, totalWeight = 0
                          for (const exp of selectedAccountExposures) {
                            const data = ncSpendableByAccount[exp.accountId]
                            if (data?.orderCashPct !== null && exp.portfolioValue > 0) {
                              sum += data.orderCashPct * exp.portfolioValue
                              totalWeight += exp.portfolioValue
                            }
                          }
                          return totalWeight > 0 ? sum / totalWeight : null
                        })()

                        return (
                          <>
                            <tr
                              className="hover:bg-gray-50 bg-green-50/50 cursor-pointer"
                              onClick={() => setNcSpendableExpanded(prev => {
                                const next = new Set(prev)
                                if (next.has('wouldbe')) next.delete('wouldbe')
                                else next.add('wouldbe')
                                return next
                              })}
                            >
                              <td className="px-4 py-3 text-sm">
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900">NC Spendable Cash %</td>
                              {accountExposures.map(exp => {
                                const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                const data = ncSpendableByAccount[exp.accountId]
                                const hasChange = data?.orderCashPct !== null && Math.abs(data.orderCashPct) > 0.0001
                                return (
                                  <td key={exp.accountNumber} className={`px-4 py-3 text-right text-sm ${
                                    !isSelected ? 'text-gray-300' : hasChange ? 'font-medium text-primary-700' : ''
                                  }`}>
                                    {formatPercent(data?.ncSpendablePct)}
                                  </td>
                                )
                              })}
                              <td className="px-4 py-3 text-right text-sm font-medium bg-blue-50">
                                {formatPercent(wtdAvgNcSpendable)}
                              </td>
                            </tr>

                            {isExpanded && (
                              <>
                                <tr className="bg-green-50/30">
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-sm text-gray-600 pl-8">Cash %</td>
                                  {accountExposures.map(exp => {
                                    const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                    return (
                                      <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                        {formatPercent(exp.cashPct)}
                                      </td>
                                    )
                                  })}
                                  <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                                    {formatPercent(calculateWeightedAvg(exp => exp.cashPct))}
                                  </td>
                                </tr>

                                <tr className="bg-green-50/30">
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-sm text-gray-600 pl-8">Min Cash %</td>
                                  {accountExposures.map(exp => {
                                    const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                    const data = ncSpendableByAccount[exp.accountId]
                                    return (
                                      <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                        {formatPercent(data?.minCashPct != null ? -data.minCashPct : null)}
                                      </td>
                                    )
                                  })}
                                  <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                                    {formatPercent(wtdAvgMinCash != null ? -wtdAvgMinCash : null)}
                                  </td>
                                </tr>

                                <tr
                                  className="bg-green-50/30 cursor-pointer hover:bg-green-50/50"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setNcArtificialExpanded(prev => {
                                      const next = new Set(prev)
                                      if (next.has('wouldbe')) next.delete('wouldbe')
                                      else next.add('wouldbe')
                                      return next
                                    })
                                  }}
                                >
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-sm text-gray-600 pl-8">
                                    <span title="Cash reserved for inverse covered calls (short stock + long call).">
                                      NC Artificial Cash %
                                    </span>
                                  </td>
                                  {accountExposures.map(exp => {
                                    const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                    const data = ncSpendableByAccount[exp.accountId]
                                    return (
                                      <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                        {formatPercent(data?.ncArtificialPct != null ? -data.ncArtificialPct : null)}
                                      </td>
                                    )
                                  })}
                                  <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                                    {formatPercent(wtdAvgNcArtificial != null ? -wtdAvgNcArtificial : null)}
                                  </td>
                                </tr>

                                {isArtificialExpanded && (() => {
                                  const allUnderlyings = new Set<string>()
                                  for (const exp of accountExposures) {
                                    const data = ncArtificialCashByAccount[exp.accountId]
                                    data?.items.forEach(item => allUnderlyings.add(item.underlying))
                                  }

                                  return Array.from(allUnderlyings).sort().map(underlyingSymbol => (
                                    <tr key={`artificial-wouldbe-${underlyingSymbol}`} className="bg-green-50/20">
                                      <td className="px-4 py-1"></td>
                                      <td className="px-4 py-1 text-xs text-gray-500 pl-16">{underlyingSymbol}</td>
                                      {accountExposures.map(exp => {
                                        const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                        const data = ncArtificialCashByAccount[exp.accountId]
                                        const item = data?.items.find(i => i.underlying === underlyingSymbol)
                                        const pct = item && exp.portfolioValue > 0
                                          ? item.artificialCash / exp.portfolioValue
                                          : null
                                        return (
                                          <td key={exp.accountNumber} className={`px-4 py-1 text-right text-xs ${isSelected ? 'text-gray-400' : 'text-gray-300'}`}>
                                            {pct !== null ? formatPercent(-pct) : '-'}
                                          </td>
                                        )
                                      })}
                                      <td className="px-4 py-1 bg-blue-50/30"></td>
                                    </tr>
                                  ))
                                })()}

                                <tr className="bg-green-50/30">
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-sm text-gray-600 pl-8">Cash from orders</td>
                                  {accountExposures.map(exp => {
                                    const isSelected = selectedAccountIds?.has(exp.accountId) ?? true
                                    const data = ncSpendableByAccount[exp.accountId]
                                    return (
                                      <td key={exp.accountNumber} className={`px-4 py-2 text-right text-sm ${isSelected ? 'text-gray-500' : 'text-gray-300'}`}>
                                        {formatPercent(data?.orderCashPct != null ? -data.orderCashPct : null)}
                                      </td>
                                    )
                                  })}
                                  <td className="px-4 py-2 text-right text-sm text-gray-500 bg-blue-50/50">
                                    {formatPercent(wtdAvgOrderCash != null ? -wtdAvgOrderCash : null)}
                                  </td>
                                </tr>
                              </>
                            )}
                          </>
                        )
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}
